// src/utils/api.js
import axios from 'axios';
import API_ENDPOINTS from '../config/apiConfig';
import { BASE_URL, FASTIFY_BASE_URL } from '../config/apiConfig';
import tokenRefreshManager from './tokenRefresh';
import { getEmulatedUserForApi } from './emulation';

// Create axios instance
export const api = axios.create({
    baseURL: BASE_URL, // Use the configured BASE_URL from apiConfig
});

// Resolve the freshest access/id tokens: prefer the (possibly just refreshed)
// Amplify session, fall back to whatever is in localStorage so a transient
// refresh failure doesn't strip auth headers off the request entirely.
const resolveAuthTokens = async (options) => {
    let session = null;
    try {
        session = await tokenRefreshManager.ensureValidToken(options);
    } catch (error) {
        if (error.isSessionExpired) {
            throw error;
        }
        // Transient failure — continue with stored tokens and let the
        // response interceptor deal with any auth error.
    }

    let accessToken = session?.tokens?.accessToken?.toString();
    let idToken = session?.tokens?.idToken?.toString();

    if (!accessToken) {
        const accessTokenKey = Object.keys(localStorage).find(key => key.endsWith('.accessToken'));
        accessToken = accessTokenKey ? localStorage.getItem(accessTokenKey) : null;
    }
    if (!idToken) {
        const idTokenKey = Object.keys(localStorage).find(key => key.endsWith('.idToken'));
        idToken = idTokenKey ? localStorage.getItem(idTokenKey) : null;
    }

    return { accessToken, idToken };
};

const applyAuthHeaders = (config, { accessToken, idToken }) => {
    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (idToken) {
        config.headers['x-id-token'] = idToken;
    }
};

// Interceptor for adding authorization tokens and user ID
api.interceptors.request.use(async (config) => {
    try {
        const { accessToken, idToken } = await resolveAuthTokens();
        applyAuthHeaders(config, { accessToken, idToken });

        // Check if we're emulating another user
        const emulatedUser = getEmulatedUserForApi();

        if (emulatedUser) {
            // Use emulated user's ID and email for API requests
            config.headers['x-user-id'] = emulatedUser.userId;
            config.headers['x-user-email'] = emulatedUser.userEmail;
            config.headers['x-emulating'] = 'true'; // Flag to indicate emulation mode
        } else if (accessToken) {
            // Extract user ID and email from access token for x-user-id and x-user-email headers (fallback)
            try {
                const payloadBase64 = accessToken.split('.')[1];
                const payload = JSON.parse(atob(payloadBase64));
                const userId = payload.username || payload.sub || payload['cognito:username'];
                const userEmail = payload.email || payload['cognito:email'] || payload['preferred_username'];

                if (userId) {
                    config.headers['x-user-id'] = userId;
                }
                if (userEmail) {
                    config.headers['x-user-email'] = userEmail;
                }
            } catch (jwtError) {
                // Could not extract user info from access token
            }
        }
        return config;
    } catch (error) {
        console.error('Failed to ensure valid token for API request:', error);
        // Continue with request anyway - let the response interceptor handle auth errors
        return config;
    }
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor to handle token expiration
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        
        // If we get a 401/403 and haven't already tried to refresh
        if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                // The server rejected our token even though it looked valid
                // locally — force a refresh and retry with BOTH new tokens
                // (fastify routes authenticate via x-id-token, not just the
                // Authorization header).
                const tokens = await resolveAuthTokens({ force: true });
                applyAuthHeaders(originalRequest, tokens);

                // Retry the original request
                return api(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed during API retry:', refreshError);
                // Only kick the user to login when the session is genuinely
                // dead (refresh token expired/revoked). Transient refresh
                // failures just surface the original error and the session
                // recovers on a later request.
                if (refreshError.isSessionExpired && window.location.pathname !== '/login') {
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        }
        
        return Promise.reject(error);
    }
);

// Implement caching for frequently used API calls
const apiCache = {
  // Store chart of accounts per orgId
  chartOfAccounts: {}, // { orgId: { data: [], timestamp: null } }
  cacheExpiry: 5 * 60 * 1000 // 5 minutes in milliseconds
};

// Fetch Chart of Accounts for a specific organization
// options.includeDeleted=true asks the backend for soft-deleted rows too
// (powers the "Show deleted" toggle + restore on the COA page).
export const getChartOfAccounts = async (orgId, locationId, options = {}) => {
  if (!orgId) {
    console.error('orgId is required to fetch chart of accounts.');
    return [];
  }

  const includeDeleted = Boolean(options && options.includeDeleted);
  // Cache keys are always strings prefixed by the orgId so invalidation can
  // match on String(orgId) / `${orgId}_` regardless of the caller's id type.
  const cacheKey = `${orgId}${locationId ? `_${locationId}` : ''}${includeDeleted ? '_deleted' : ''}`;
  const now = Date.now();
  const cachedEntry = apiCache.chartOfAccounts[cacheKey];

  // Check if we have a valid cached result for this orgId/locationId combo
  if (cachedEntry && 
      cachedEntry.timestamp && 
      (now - cachedEntry.timestamp) < apiCache.cacheExpiry) {
    return cachedEntry.data;
  }
  
  try {
    // Build URL with both orgId and locationId if available
    let url = `${API_ENDPOINTS.GET_CHARTOFACCOUNTS}?orgId=${orgId}`;
    if (locationId) {
      url += `&locationId=${locationId}`;
    }
    if (includeDeleted) {
      url += '&includeDeleted=1';
    }

    const response = await api.get(url);
    
    let data = response.data;
    
    // Cache the result for this specific orgId/locationId combo
    apiCache.chartOfAccounts[cacheKey] = {
      data: data,
      timestamp: now
    };
    
    return data;
  } catch (error) {
    console.error(`Error fetching chart of accounts for orgId ${orgId}, locationId ${locationId || 'none'}:`, error);
    // If there's an error but we have cached data for this combo, return it even if expired
    if (cachedEntry && cachedEntry.data) {
      // Returning stale cached chart of accounts due to API error
      return cachedEntry.data;
    }
    // Otherwise return empty array
    return [];
  }
};

// Create a new Chart of Account entry
export const createChartOfAccount = async (orgId, data) => {
  if (!orgId || !data) {
      console.error('orgId and data are required for creating chart of account.');
      throw new Error('Missing parameters for createChartOfAccount');
  }
  try {
      const url = `${BASE_URL}/chartOfAccounts`;
      const payload = { ...data, orgId };
      const response = await api.post(url, payload);

      // Invalidate all cache entries for this orgId (including location-specific ones)
      Object.keys(apiCache.chartOfAccounts).forEach(key => {
          if (String(key) === String(orgId) || key.startsWith(`${orgId}_`)) {
              // Invalidating chart of accounts cache
              delete apiCache.chartOfAccounts[key];
          }
      });

      return response.data;
  } catch (error) {
      console.error(`Error creating chart of account:`, error);
      throw error; // Re-throw the error to be handled by the caller
  }
};

// Update a specific Chart of Account entry
export const updateChartOfAccount = async (accountId, orgId, data) => {
  if (!accountId || !orgId || !data) {
      console.error('accountId, orgId, and data are required for updating chart of account.');
      throw new Error('Missing parameters for updateChartOfAccount');
  }
  try {
      // Construct the URL dynamically for the specific account
      const url = `${BASE_URL}/chartOfAccounts/${accountId}`; 
      const response = await api.put(url, data); // Assuming PUT request updates the item

      // Invalidate all cache entries for this orgId (including location-specific ones)
      Object.keys(apiCache.chartOfAccounts).forEach(key => {
          if (String(key) === String(orgId) || key.startsWith(`${orgId}_`)) {
              // Invalidating chart of accounts cache
              delete apiCache.chartOfAccounts[key];
          }
      });

      return response.data;
  } catch (error) {
      console.error(`Error updating chart of account ${accountId}:`, error);
      throw error; // Re-throw the error to be handled by the caller
  }
};

// Delete a Chart of Account entry
export const deleteChartOfAccount = async (accountId, orgId) => {
  if (!accountId) {
      console.error('accountId is required for deleting chart of account.');
      throw new Error('Missing accountId for deleteChartOfAccount');
  }
  try {
      const url = `${BASE_URL}/chartOfAccounts?id=${accountId}`;
      const response = await api.delete(url);

      // Invalidate all cache entries for this orgId (including location-specific ones)
      if (orgId) {
          Object.keys(apiCache.chartOfAccounts).forEach(key => {
              if (String(key) === String(orgId) || key.startsWith(`${orgId}_`)) {
                  // Invalidating chart of accounts cache
                  delete apiCache.chartOfAccounts[key];
              }
          });
      }

      return response.data;
  } catch (error) {
      console.error(`Error deleting chart of account ${accountId}:`, error);
      throw error; // Re-throw the error to be handled by the caller
  }
};

// Bulk update Chart of Account entries
export const updateMultipleChartOfAccounts = async (orgId, accountIds, field, value) => {
    if (!orgId || !accountIds || accountIds.length === 0 || !field || value === undefined) {
        console.error('Missing parameters for bulk update chart of accounts.');
        throw new Error('Missing parameters for updateMultipleChartOfAccounts');
    }
    try {
        // Use POST to the base endpoint for bulk actions
        const url = API_ENDPOINTS.GET_CHARTOFACCOUNTS; // e.g., /api/chartOfAccounts
        const payload = {
            action: 'bulkUpdate', // Add an action indicator
            orgId: orgId,
            ids: accountIds,      // Array of IDs to update
            field: field,       // Field to update ('accountType' or 'accountGroup')
            value: value        // New value for the field
        };

        const response = await api.post(url, payload);

        // Invalidate all cache entries for this orgId (including location-specific ones)
        Object.keys(apiCache.chartOfAccounts).forEach(key => {
            if (String(key) === String(orgId) || key.startsWith(`${orgId}_`)) {
                // Invalidating chart of accounts cache
                delete apiCache.chartOfAccounts[key];
            }
        });

        return response.data;
    } catch (error) {
        console.error(`Error bulk updating chart of accounts:`, error);
        throw error; // Re-throw the error to be handled by the caller
    }
};

// ============================================
// Chart of Account Departments API
// ============================================

// Fetch Chart of Account Departments for a specific organization
export const getChartOfAccountDepartments = async (orgId, locationId) => {
    if (!orgId) {
        console.error('orgId is required to fetch chart of account departments.');
        return [];
    }
    try {
        const url = `${BASE_URL}/chartOfAccountsDepartments?orgId=${orgId}${locationId ? `&locationId=${locationId}` : ''}`;
        const response = await api.get(url);
        return response.data || [];
    } catch (error) {
        console.error(`Error fetching chart of account departments for orgId ${orgId}:`, error);
        return [];
    }
};

// Create a new Chart of Account Department
export const createChartOfAccountDepartment = async (orgId, data) => {
    if (!orgId || !data) {
        console.error('orgId and data are required for creating chart of account department.');
        throw new Error('Missing parameters for createChartOfAccountDepartment');
    }
    try {
        const url = `${BASE_URL}/chartOfAccountsDepartments`;
        const payload = { ...data, orgId };
        const response = await api.post(url, payload);
        return response.data;
    } catch (error) {
        console.error(`Error creating chart of account department:`, error);
        throw error;
    }
};

// Bulk presentation-order save (drag-and-drop on the P&L Mapping tab):
// assigns displayOrder 1..N to the given department ids in array order.
// Departments not listed stay unordered and fall back to the account-number
// presentation order AFTER every ordered one. Returns a light
// { success, updated } — callers refetch departments once if they need the
// refreshed rows.
export const reorderChartOfAccountDepartments = async (orgId, orderedIds, locationId = null) => {
    // locationId null = the org-wide ("All Locations") order. With a
    // locationId the call REPLACES that location's override rows — an empty
    // orderedIds clears them so the location inherits the org order again.
    if (!orgId || !Array.isArray(orderedIds) || (orderedIds.length === 0 && locationId == null)) {
        throw new Error('Missing parameters for reorderChartOfAccountDepartments');
    }
    const response = await api.post(`${BASE_URL}/chartOfAccountsDepartments`, {
        action: 'reorder',
        orgId,
        orderedIds,
        ...(locationId != null ? { locationId } : {}),
    });
    return response.data || [];
};

// Partial update of a Chart of Account Department (usaliSection — the USALI
// band the P&L Dept/GOP views place the department in; presentationName — the
// statement caption, shared by departments that print on one line)
export const updateChartOfAccountsDepartment = async (id, data) => {
    if (!id || !data) {
        console.error('id and data are required for updating chart of account department.');
        throw new Error('Missing parameters for updateChartOfAccountsDepartment');
    }
    try {
        const response = await api.patch(`${BASE_URL}/chartOfAccountsDepartments?id=${id}`, data);
        return response.data;
    } catch (error) {
        console.error(`Error updating chart of account department ${id}:`, error);
        throw error;
    }
};

// % Rev / GOP pairing links: expense dept/account → revenue account(s)
export const getCoaRevenueBasisMappings = async (orgId) => {
    if (!orgId) return [];
    try {
        const response = await api.get(`${BASE_URL}/coaRevenueBasisMappings?orgId=${orgId}`);
        return response.data || [];
    } catch (error) {
        console.error(`Error fetching COA revenue basis mappings for orgId ${orgId}:`, error);
        return [];
    }
};

/**
 * Replace the revenue-account links for one source (a COA department or a
 * single account). Pass an empty array to clear the source's links.
 */
export const replaceCoaRevenueBasisMappings = async (orgId, source, revenueChartOfAccountIds) => {
    if (!orgId || !source || (source.chartOfAccountDepartmentId == null && source.chartOfAccountId == null)) {
        throw new Error('orgId and a source (chartOfAccountDepartmentId or chartOfAccountId) are required');
    }
    const response = await api.post(`${BASE_URL}/coaRevenueBasisMappings`, {
        action: 'replace',
        orgId,
        ...(source.chartOfAccountDepartmentId != null
            ? { chartOfAccountDepartmentId: source.chartOfAccountDepartmentId }
            : { chartOfAccountId: source.chartOfAccountId }),
        revenueChartOfAccountIds: revenueChartOfAccountIds || [],
    });
    return response.data;
};

// P&L KPI definitions (Chart of Accounts → P&L KPIs tab): ratio rows on the
// Financial P&L. Shape: azfApi/utils/pnlKpis.js.
export const getPnlKpiDefinitions = async (orgId) => {
    if (!orgId) return [];
    try {
        const response = await api.get(`${BASE_URL}/pnlKpiDefinitions?orgId=${orgId}`);
        return response.data || [];
    } catch (error) {
        console.error(`Error fetching P&L KPI definitions for orgId ${orgId}:`, error);
        return [];
    }
};

/** Create (no id) or update (id) one KPI definition. */
export const savePnlKpiDefinition = async (orgId, definition) => {
    const response = await api.post(`${BASE_URL}/pnlKpiDefinitions`, { ...definition, orgId });
    return response.data;
};

export const deletePnlKpiDefinition = async (orgId, id) => {
    const response = await api.delete(`${BASE_URL}/pnlKpiDefinitions/${id}?orgId=${orgId}`);
    return response.data;
};

// Order every KPI row — built-ins included — as it reads on the P&L:
// orderedKeys = ['builtin:occupancy' | 'builtin:adr' | 'builtin:revpar' | 'kpi:<id>', …]
// in their new order (pages/kpiOrder.js). Returns the full sorted list.
export const reorderPnlKpiDefinitions = async (orgId, orderedKeys) => {
    const response = await api.post(`${BASE_URL}/pnlKpiDefinitions/reorder`, { orgId, orderedKeys });
    return response.data;
};

// Fetch Chart of Account Sub-Departments for a specific organization or department
export const getChartOfAccountSubDepartments = async (orgId, departmentId = null) => {
    if (!orgId) {
        console.error('orgId is required to fetch chart of account sub-departments.');
        return [];
    }
    try {
        let url = `${BASE_URL}/chartOfAccountsSubDepartments?orgId=${orgId}`;
        if (departmentId) {
            url = `${BASE_URL}/chartOfAccountsSubDepartments?departmentId=${departmentId}`;
        }
        const response = await api.get(url);
        return response.data || [];
    } catch (error) {
        console.error(`Error fetching chart of account sub-departments:`, error);
        return [];
    }
};

// Create a new Chart of Account Sub-Department
export const createChartOfAccountSubDepartment = async (orgId, data) => {
    if (!orgId || !data || !data.chartOfAccountDepartmentId) {
        console.error('orgId, data, and chartOfAccountDepartmentId are required for creating chart of account sub-department.');
        throw new Error('Missing parameters for createChartOfAccountSubDepartment');
    }
    try {
        const url = `${BASE_URL}/chartOfAccountsSubDepartments`;
        const payload = { ...data, orgId };
        const response = await api.post(url, payload);
        return response.data;
    } catch (error) {
        console.error(`Error creating chart of account sub-department:`, error);
        throw error;
    }
};

export const getOrgConfig = async () => {
    return api.get(API_ENDPOINTS.GET_ORG_CONFIG).then((response) => response.data);
};

export const updateOrgConfig = async (data) => {
    return api.patch(API_ENDPOINTS.GET_ORG_CONFIG, data).then((response) => response.data);
};

/**
 * Get consolidated initialization data (BFF pattern)
 * Fetches user settings, organizations, and org config in a single optimized backend call
 * This replaces multiple separate calls on page refresh for better performance
 *
 * @returns {Promise<{userSettings, organizations, orgConfig}>}
 */
export const getInitData = async () => {
    return api.get(API_ENDPOINTS.INIT_DATA).then((response) => response.data);
};

export const getUserSettings = async (email) => {
    return api.get(API_ENDPOINTS.GET_USER_SETTINGS(email)).then((response) => response.data);
};

export const postItem = async (data) => {
    return api.post(API_ENDPOINTS.POST_ITEM, data).then((response) => response.data);
};

// DEMO org: file viewers must never expose a real customer document — every
// PDF request resolves to this redacted sample night audit instead. Shared by
// the PBI link viewer (fetchPdfFromBlob/fetchTextFromBlob) and the native app
// file viewer (fetchBlobViaProxy).
// Opened through /api/pbiPdfProxy, which adds the SAS server-side — never
// ship a storage token in the bundle (the old value here was a container-wide
// read SAS for custdev1).
export const DEMO_REDACTED_PDF_URL = 'https://lodgeicblob01.blob.core.windows.net/custdev1/demo/audit_Final%20Audit%2008.19.2025.pdf';
export const isDemoOrg = () => localStorage.getItem('currentOrgCode') === 'DEMO';

export const fetchPdfFromBlob = async (blobUrl, embedToken) => {
    if (isDemoOrg()) {
      blobUrl = DEMO_REDACTED_PDF_URL;
    }

    try {
        // Decode any HTML entities in the URL (convert &amp; to &)
        const decodedUrl = new DOMParser().parseFromString(blobUrl, 'text/html').documentElement.textContent;

        // Use our backend proxy to fetch the PDF
        const response = await api.post(API_ENDPOINTS.GET_PDF_PROXY, {
            url: decodedUrl,
            embedToken
        }, {
            responseType: 'blob',
            timeout: 30000 // 30 second timeout - aligned with backend proxy
        });
        

        if (!response.data) {
            throw new Error('No data received from proxy');
        }

        // response.data is already a Blob when using responseType: 'blob'
        // Ensure correct MIME type for PDF rendering
        let blob = response.data;
        console.log('📄 fetchPdfFromBlob: Received blob', {
            size: blob.size,
            type: blob.type,
            isBlob: blob instanceof Blob
        });

        if (blob.type !== 'application/pdf') {
            console.log('📄 fetchPdfFromBlob: Re-typing blob from', blob.type, 'to application/pdf');
            // Use slice() to properly re-type the blob without corrupting contents
            blob = blob.slice(0, blob.size, 'application/pdf');
        }
        const createdBlobUrl = URL.createObjectURL(blob);
        console.log('📄 fetchPdfFromBlob: Created blob URL', createdBlobUrl);

        return createdBlobUrl;
    } catch (error) {
        console.error('❌ Error in fetchPdfFromBlob:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data instanceof Blob ? 'Blob data' : error.response?.data
        });
        
        // If it's a timeout error, provide a more specific message
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            throw new Error('Request timeout - the PDF server may be slow. Please try again or open in new tab.');
        }
        
        throw error;
    }
};

export const fetchTextFromBlob = async (blobUrl, embedToken) => {
    // Override URL for DEMO org - always fetch the demo PDF
    const demoOverride = isDemoOrg();
    if (demoOverride) {
      blobUrl = DEMO_REDACTED_PDF_URL;
    }

    try {
        // Decode any HTML entities in the URL (convert &amp; to &)
        const decodedUrl = new DOMParser().parseFromString(blobUrl, 'text/html').documentElement.textContent;

        // Use our backend proxy to fetch the file
        // For DEMO org, fetch as blob since it's a PDF
        const response = await api.post(API_ENDPOINTS.GET_PDF_PROXY, {
            url: decodedUrl,
            embedToken
        }, {
            responseType: demoOverride ? 'blob' : 'text'
        });

        if (!response.data) {
            throw new Error('No data received from proxy');
        }

        // Create a blob URL for the content
        if (demoOverride) {
            // For DEMO org, return PDF blob
            let blob = response.data;
            if (blob.type !== 'application/pdf') {
                blob = blob.slice(0, blob.size, 'application/pdf');
            }
            return URL.createObjectURL(blob);
        } else {
            // For regular text files
            const blob = new Blob([response.data], { type: 'text/plain' });
            return URL.createObjectURL(blob);
        }
    } catch (error) {
        console.error('Error fetching text file:', error);
        throw error;
    }
};

// Get all users (legacy - returns flat array for backwards compatibility)
export const getAllUsers = async () => {
  try {
    const response = await api.get(`${BASE_URL}/userSettings`);
    // Handle new paginated response format
    if (response.data && response.data.users && Array.isArray(response.data.users)) {
      return response.data.users;
    }
    // Legacy format: Ensure we always return an array
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && typeof response.data === 'object') {
      // If it's a single user object, wrap it in an array
      return [response.data];
    } else {
      // If it's neither an array nor an object, return an empty array
      console.warn('Unexpected response type from userSettings API:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching all users:', error);
    throw error;
  }
};

// Get users with pagination support
export const getUsersPaginated = async ({ limit = 50, offset = 0, search = '', includeDeleted = false } = {}) => {
  try {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (search) params.append('search', search);
    if (includeDeleted) params.append('includeDeleted', 'true');

    const response = await api.get(`${BASE_URL}/userSettings?${params.toString()}`);

    // Handle new paginated response format
    if (response.data && response.data.users && response.data.pagination) {
      return response.data;
    }

    // Legacy format fallback
    const users = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
    return {
      users,
      pagination: {
        total: users.length,
        offset: 0,
        limit: users.length,
        hasMore: false
      }
    };
  } catch (error) {
    console.error('Error fetching paginated users:', error);
    throw error;
  }
};

// Search users for mentions (optimized for mentions dropdown)
export const searchUsersForMentions = async (query = '', orgCode = null, orgId = null) => {
  try {
    const params = new URLSearchParams();
    if (query) params.append('search', query);
    if (orgCode) params.append('orgCode', orgCode);
    if (orgId) params.append('orgId', orgId);
    params.append('limit', '20'); // Limit for performance
    
    const response = await api.get(`${BASE_URL}/userSettings/search?${params.toString()}`);

    // Handle paginated response format: { users: [...], pagination: {...} }
    // Transform and filter the response for mentions
    let users;
    if (response.data && response.data.users && Array.isArray(response.data.users)) {
      users = response.data.users;
    } else {
      users = Array.isArray(response.data) ? response.data : [response.data].filter(Boolean);
    }
    
    return users
      .filter(user => user && !user.isDeleted && user.email)
      .filter(user => {
        // Filter by organization if orgId is provided (fallback to orgId for backward compatibility)
        const orgIdentifier = orgId || orgCode;
        if (!orgIdentifier) return true;
        return user.orgAccess && (user.orgAccess[orgId] || user.orgAccess[orgCode]);
      })
      .map(user => ({
        id: user.id || user.email,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email.split('@')[0],
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        initials: getInitialsFromName(`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email)
      }));
  } catch (error) {
    console.error('Error searching users for mentions:', error);
    // If search endpoint doesn't exist, fall back to getAllUsers with org filtering
    if (error.response?.status === 404) {
      const allUsers = await getAllUsers();
      return allUsers
        .filter(user => user && !user.isDeleted && user.email)
        .filter(user => {
          // Filter by organization using both orgCode and orgId for compatibility
          const orgIdentifier = orgId || orgCode;
          if (orgIdentifier && (!user.orgAccess || (!user.orgAccess[orgId] && !user.orgAccess[orgCode]))) return false;
          
          // Filter by search query if provided
          if (!query) return true;
          const searchQuery = query.toLowerCase();
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          return (
            user.email.toLowerCase().includes(searchQuery) ||
            fullName.toLowerCase().includes(searchQuery) ||
            (user.firstName && user.firstName.toLowerCase().includes(searchQuery)) ||
            (user.lastName && user.lastName.toLowerCase().includes(searchQuery))
          );
        })
        .slice(0, 20) // Limit results
        .map(user => ({
          id: user.id || user.email,
          name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email.split('@')[0],
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          initials: getInitialsFromName(`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email)
        }));
    }
    throw error;
  }
};

// Helper function to get initials from name
const getInitialsFromName = (name) => {
  if (!name) return '??';
  const parts = name.split(' ').filter(part => part.length > 0);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Get user's custom tags
export const getUserTags = async (userEmail) => {
  try {
    const userSettings = await getUserSettings(userEmail);
    return userSettings?.customTags || [];
  } catch (error) {
    console.error('Error fetching user tags:', error);
    return [];
  }
};

// Save user's custom tags
export const saveUserTags = async (userEmail, tags) => {
  try {
    const userSettings = await getUserSettings(userEmail);
    const updatedUser = {
      ...userSettings,
      customTags: tags
    };
    
    return await saveUserSettings(updatedUser);
  } catch (error) {
    console.error('Error saving user tags:', error);
    throw error;
  }
};

// Add new tag to user's collection
export const addUserTag = async (userEmail, newTag, color = null) => {
  try {
    const currentTags = await getUserTags(userEmail);
    
    // Normalize tag (lowercase, trim, remove spaces)
    const normalizedTag = (typeof newTag === 'string' ? newTag : newTag.label)
      .toLowerCase().trim().replace(/\s+/g, '-');
    
    // Check if tag already exists
    if (currentTags.some(tag => tag.id === normalizedTag)) {
      return currentTags; // Tag already exists, return current tags
    }
    
    // Default color palette for tags (starting with orange as default)
    const defaultColors = [
      '#f97316', // Orange (default)
      '#8b5cf6', // Purple
      '#06b6d4', // Cyan
      '#10b981', // Emerald
      '#f59e0b', // Amber
      '#ef4444', // Red
      '#3b82f6', // Blue
      '#ec4899', // Pink
      '#84cc16', // Lime
      '#6366f1'  // Indigo
    ];
    
    // Auto-assign color if not provided
    const tagColor = color || defaultColors[currentTags.length % defaultColors.length];
    
    // Add new tag
    const updatedTags = [
      ...currentTags,
      {
        id: normalizedTag,
        label: typeof newTag === 'string' ? newTag.trim() : newTag.label.trim(),
        color: tagColor,
        createdAt: new Date().toISOString(),
        usageCount: 1
      }
    ];
    
    await saveUserTags(userEmail, updatedTags);
    return updatedTags;
  } catch (error) {
    console.error('Error adding user tag:', error);
    throw error;
  }
};

// Update tag color
export const updateTagColor = async (userEmail, tagId, newColor) => {
  try {
    const currentTags = await getUserTags(userEmail);
    const updatedTags = currentTags.map(tag => 
      tag.id === tagId 
        ? { ...tag, color: newColor, updatedAt: new Date().toISOString() }
        : tag
    );
    
    await saveUserTags(userEmail, updatedTags);
    return updatedTags;
  } catch (error) {
    console.error('Error updating tag color:', error);
    throw error;
  }
};

// Delete tag from user's collection
export const deleteUserTag = async (userEmail, tagId) => {
  try {
    const currentTags = await getUserTags(userEmail);
    const updatedTags = currentTags.filter(tag => tag.id !== tagId);
    
    await saveUserTags(userEmail, updatedTags);
    return updatedTags;
  } catch (error) {
    console.error('Error deleting user tag:', error);
    throw error;
  }
};

// Increment tag usage count
export const incrementTagUsage = async (userEmail, tagId) => {
  try {
    const currentTags = await getUserTags(userEmail);
    const updatedTags = currentTags.map(tag => 
      tag.id === tagId 
        ? { ...tag, usageCount: (tag.usageCount || 0) + 1, lastUsed: new Date().toISOString() }
        : tag
    );
    
    await saveUserTags(userEmail, updatedTags);
    return updatedTags;
  } catch (error) {
    console.error('Error incrementing tag usage:', error);
    throw error;
  }
};

// =============================================================================
// DASHBOARD PREFERENCES API FUNCTIONS
// =============================================================================

// Get user's dashboard preferences
export const getDashboardPreferences = async (userEmail) => {
  try {
    const userSettings = await getUserSettings(userEmail);
    return userSettings?.dashboardPreferences || {};
  } catch (error) {
    console.error('Error fetching dashboard preferences:', error);
    return {};
  }
};

// Save user's dashboard preferences (e.g., default date range)
export const saveDashboardPreferences = async (userEmail, preferences) => {
  try {
    const userSettings = await getUserSettings(userEmail);
    const updatedUser = {
      ...userSettings,
      dashboardPreferences: {
        ...(userSettings?.dashboardPreferences || {}),
        ...preferences
      }
    };

    return await saveUserSettings(updatedUser);
  } catch (error) {
    console.error('Error saving dashboard preferences:', error);
    throw error;
  }
};

// Save user's UI preferences (Settings → Appearance → Layout). Stored on the
// Cosmos user doc as `uiPreferences` and projected by initData.
// A `null` value in the patch removes that key from the stored preferences
// (used to clear the New-look personalisation when a user goes back to Classic).
export const mergeUiPreferences = (current, patch) => {
  const next = { ...(current || {}) };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (value === null) delete next[key];
    else next[key] = value;
  });
  return next;
};

export const saveUiPreferences = async (userEmail, preferences) => {
  const userSettings = await getUserSettings(userEmail);
  return saveUserSettings({
    ...userSettings,
    uiPreferences: mergeUiPreferences(userSettings?.uiPreferences, preferences),
  });
};

// =============================================================================
// NOTIFICATION SYSTEM API FUNCTIONS
// =============================================================================

// Get user notifications
export const getUserNotifications = async (userEmail, limit = 50, offset = 0) => {
  try {
    const params = new URLSearchParams();
    params.append('userEmail', userEmail);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    
    const response = await api.get(`${BASE_URL}/notifications?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching user notifications:', error);
    throw error;
  }
};

// Get unread notification count
export const getUnreadNotificationCount = async (userEmail) => {
  try {
    const response = await api.get(`${BASE_URL}/notifications?userEmail=${encodeURIComponent(userEmail)}&unreadCount=true`);
    return response.data?.count || 0;
  } catch (error) {
    console.error('Error fetching unread notification count:', error);
    return 0;
  }
};

// Mark notification as read
export const markNotificationAsRead = async (notificationId, userEmail) => {
  try {
    const response = await api.patch(`${BASE_URL}/notifications?notificationId=${encodeURIComponent(notificationId)}`, {
      userEmail
    });
    return response.data;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async (userEmail) => {
  try {
    const response = await api.patch(`${BASE_URL}/notifications?readAll=true`, {
      userEmail
    });
    return response.data;
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    throw error;
  }
};

// Create a notification (typically called server-side, but useful for testing)
export const createNotification = async (notificationData) => {
  try {
    const response = await api.post(`${BASE_URL}/notifications`, notificationData);
    return response.data;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (notificationId, userEmail) => {
  try {
    const response = await api.delete(`${BASE_URL}/notifications?notificationId=${encodeURIComponent(notificationId)}&userEmail=${encodeURIComponent(userEmail)}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Create or update a user. Existing users (loaded docs carry _etag) PATCH —
// the server MERGES the body over the stored doc, so partial bodies are safe
// and no client-side orgAccess rebuild is needed. The server also syncs the
// Stream Chat profile (no second client-side upsert).
export const saveUserSettings = async (userData) => {
  try {
    const payload = { ...userData, id: userData.id || userData.email };
    const isNewUser = !userData._etag;
    const response = isNewUser
      ? await api.post(`${BASE_URL}/userSettings`, payload)
      : await api.patch(`${BASE_URL}/userSettings`, payload);
    return response.data;
  } catch (error) {
    console.error('Error saving user settings:', error);
    throw error;
  }
};

// Update the signed-in user's profile fields in the Cosmos `users` container.
// The backend only accepts firstName, lastName, and profilePicture on this route.
export const updateUserProfile = async ({ firstName, lastName, profilePicture, profilePictureThumb }) => {
  try {
    const response = await api.patch(`${BASE_URL}/userProfile`, {
      firstName,
      lastName,
      profilePicture,
      profilePictureThumb,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const setChatChannelMute = async ({ cid, channelType, channelId, muted }) => {
  if (!cid && (!channelType || !channelId)) {
    throw new Error('cid or channelType/channelId is required.');
  }

  const payload = {
    cid,
    channelType,
    channelId,
    muted: !!muted,
  };
  const primaryUrl = `${FASTIFY_BASE_URL}/chatChannelMute`;

  try {
    const response = await api.post(primaryUrl, payload);
    return response.data;
  } catch (error) {
    const base = String(BASE_URL || '').replace(/\/$/, '');
    const fallbackUrl = base.endsWith('/api')
      ? `${base}/chatChannelMute`
      : `${base}/api/chatChannelMute`;

    if (!base || fallbackUrl === primaryUrl) {
      throw error;
    }

    const response = await api.post(fallbackUrl, payload);
    return response.data;
  }
};

// Soft delete a user (mark as deleted and deactivate Cognito account).
// PATCH merges server-side, so only the flag is sent.
export const deleteUserSettings = async (userId) => {
  try {
    const response = await api.patch(`${BASE_URL}/userSettings`, { id: userId, isDeleted: 1 });
    return response.data;
  } catch (error) {
    console.error('Error soft-deleting user settings:', error);
    throw error;
  }
};

// Permanently delete a user (remove from CosmosDB and delete Cognito account)
export const permanentlyDeleteUser = async (userId) => {
  try {
    const response = await api.delete(`${BASE_URL}/userSettings?id=${encodeURIComponent(userId)}&permanent=true`);
    return response.data;
  } catch (error) {
    console.error('Error permanently deleting user:', error);
    throw error;
  }
};

// Restore a deleted user (re-enables Cognito and emails a new temp password)
export const restoreUserSettings = async (userId) => {
  try {
    const response = await api.patch(`${BASE_URL}/userSettings`, { id: userId, isDeleted: 0 });
    return response.data;
  } catch (error) {
    console.error('Error restoring user settings:', error);
    throw error;
  }
};

// Convert email to valid Stream Chat user ID
// Stream Chat allows: a-z, 0-9, @, _, -
const emailToStreamChatId = (email) => {
    if (!email) return null;
    
    // Replace dots with underscores and ensure lowercase
    // Keep @ symbol as it's allowed in Stream Chat IDs
    return email.toLowerCase().replace(/\./g, '_');
};

// Stream Chat token API call
export const getStreamChatToken = async (userEmail) => {
    try {

        if (!userEmail || !userEmail.includes('@')) {
            throw new Error('Valid email is required for Stream Chat token');
        }
        
        // Convert email to valid Stream Chat ID for token request
        const streamChatId = emailToStreamChatId(userEmail);

        const response = await api.post(API_ENDPOINTS.GET_STREAM_CHAT_TOKEN, { userId: streamChatId });

        return response.data;
    } catch (error) {
        console.error('Error getting Stream Chat token:', error);
        throw error;
    }
};

// Sync all users from database to Stream Chat
export const syncAllUsersToStreamChat = async () => {
    try {

        const response = await api.post(`${API_ENDPOINTS.UPSERT_STREAM_CHAT_USERS}?syncAll=true`, {});
        

        return response.data;
    } catch (error) {
        console.error('[streamSync] Error syncing users to Stream Chat:', error);
        console.error('[streamSync] Error details:', {
            message: error.message,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data
        });
        throw error;
    }
};

// Admin Password Reset function
export const adminPasswordReset = async (userEmail, customPassword = null, sendEmail = true) => {
  try {

    const payload = {
      userEmail,
      customPassword,
      sendEmail
    };
    
    const response = await api.post(API_ENDPOINTS.ADMIN_PASSWORD_RESET, payload);
    

    return response.data;
  } catch (error) {
    console.error('Error in admin password reset:', error);
    throw error;
  }
};

// Log user activity
export const logUserActivity = async (activityData) => {
  try {
    // Basic validation on the frontend
    if (!activityData || !activityData.userEmail || !activityData.urlPath) {
      console.warn('logUserActivity called with missing userEmail or urlPath.', activityData);
      return; // Don't send incomplete data
    }

  } catch (error) {
    // Log the error but don't throw it, as logging failure shouldn't break the user experience
    console.error('Error logging user activity:', error);
  }
};

// Revenue Types API functions
export const getRevenueTypes = async (orgId, locationId = null, includeDeleted = false) => {
  try {
    const response = await api.get(API_ENDPOINTS.GET_REVENUE_TYPES_BY_ORG(orgId, locationId, includeDeleted));
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue types:', error);
    throw error;
  }
};

export const updateRevenueType = async (id, orgId, updateData) => {
  try {
    const url = `${API_ENDPOINTS.GET_REVENUE_TYPES}/${id}`;
    const response = await api.put(url, { ...updateData, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error updating revenue type ${id}:`, error);
    throw error;
  }
};

// Bulk-update one field across many revenue types in a SINGLE request.
// Uses the POST bulkUpdate action so we don't fan out N parallel PUTs (which
// race the shared mssql pool and fail with "Connection is closed").
export const bulkUpdateRevenueTypes = async (orgId, locationId, ids, field, value) => {
  try {
    const url = API_ENDPOINTS.GET_REVENUE_TYPES;
    const response = await api.post(url, {
      action: 'bulkUpdate',
      orgId,
      locationId,
      ids,
      field,
      value,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating revenue types:', error);
    throw error;
  }
};

// Create one revenue type at an org+location. The route answers 409 when a
// live type with the same name already exists there — callers toast that.
export const createRevenueType = async ({ orgId, locationId, name, displayName = null, mappedType = null, chartOfAccountId = null, typeClassificationId = null, segmentGroup = null }) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_REVENUE_TYPES, {
      orgId, locationId, name, displayName, mappedType, chartOfAccountId, typeClassificationId, segmentGroup,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating revenue type:', error);
    throw error;
  }
};

// AI Revenue Type to COA Mapping suggestions
export const getAiRevenueCoaMappingSuggestions = async ({ orgId, locationId, revenueTypes, chartAccounts }) => {
  try {
    const response = await api.post(API_ENDPOINTS.AI_REVENUE_COA_MAPPING, {
      orgId, locationId, revenueTypes, chartAccounts
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI revenue COA mapping suggestions:', error);
    throw error;
  }
};

// AI Journal Entry Type to COA Mapping suggestions
export const getAiJournalCoaMappingSuggestions = async ({ orgId, locationId, journalEntryTypes, userPrompt }) => {
  try {
    const response = await api.post(API_ENDPOINTS.AI_JOURNAL_COA_MAPPING, {
      orgId, locationId, journalEntryTypes, ...(userPrompt ? { userPrompt } : {})
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI journal COA mapping suggestions:', error);
    throw error;
  }
};

export const deleteRevenueType = async (id) => {
  try {
    const url = `${API_ENDPOINTS.GET_REVENUE_TYPES}?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting revenue type ${id}:`, error);
    throw error;
  }
};

export const restoreRevenueType = async (id, orgId) => {
  try {
    const url = `${API_ENDPOINTS.GET_REVENUE_TYPES}/${id}`;
    const response = await api.put(url, { isDeleted: false, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error restoring revenue type ${id}:`, error);
    throw error;
  }
};

// Journal Entry Types API functions
export const getJournalEntryTypes = async (orgId, locationId = null, includeDeleted = false) => {
  try {
    const response = await api.get(API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES_BY_ORG(orgId, locationId, includeDeleted));
    return response.data;
  } catch (error) {
    console.error('Error fetching journal entry types:', error);
    throw error;
  }
};

export const updateJournalEntryType = async (id, orgId, updateData) => {
  try {
    const url = `${API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES}/${id}`;
    const response = await api.put(url, { ...updateData, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error updating journal entry type ${id}:`, error);
    throw error;
  }
};

export const deleteJournalEntryType = async (id) => {
  try {
    const url = `${API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES}?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting journal entry type ${id}:`, error);
    throw error;
  }
};

export const restoreJournalEntryType = async (id, orgId) => {
  try {
    const url = `${API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES}/${id}`;
    const response = await api.put(url, { isDeleted: false, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error restoring journal entry type ${id}:`, error);
    throw error;
  }
};

// One statement server-side: POST { action:'bulkUpdate', orgId, ids, field, value }
// field ∈ { category, chartOfAccountId }; value null clears. → { success, updated }
export const bulkUpdateJournalEntryTypes = async (orgId, ids, field, value) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES, {
      action: 'bulkUpdate', orgId, ids, field, value,
    });
    return response.data;
  } catch (error) {
    console.error('Error bulk updating journal entry types:', error);
    throw error;
  }
};

// Create a journal entry type by hand (normally the ETL creates them).
// 409 when a live row with the same name exists at that org+location.
export const createJournalEntryType = async ({ orgId, locationId, name, category = null, chartOfAccountId = null }) => {
  try {
    const response = await api.post(API_ENDPOINTS.GET_JOURNAL_ENTRY_TYPES, {
      orgId, locationId, name, category, chartOfAccountId,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating journal entry type:', error);
    throw error;
  }
};

// Revenue Type Budget Accounts API functions (Budget COA Mappings)
export const getRevenueTypeBudgetAccounts = async (orgId, locationId, revenueTypeId = null) => {
  try {
    let url = `${BASE_URL}/revenueTypeBudgetAccounts?orgId=${orgId}&locationId=${locationId}`;
    if (revenueTypeId) {
      url += `&revenueTypeId=${revenueTypeId}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching revenue type budget accounts:', error);
    throw error;
  }
};

export const createRevenueTypeBudgetAccount = async (mappingData) => {
  try {
    const url = `${BASE_URL}/revenueTypeBudgetAccounts`;
    const response = await api.post(url, mappingData);
    return response.data;
  } catch (error) {
    console.error('Error creating revenue type budget account:', error);
    throw error;
  }
};

export const deleteRevenueTypeBudgetAccount = async (id) => {
  try {
    const url = `${BASE_URL}/revenueTypeBudgetAccounts?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting revenue type budget account ${id}:`, error);
    throw error;
  }
};

// Replace the full set of budget COAs on one revenue type in a single round trip.
export const replaceRevenueTypeBudgetAccounts = async ({ orgId, locationId, revenueTypeId, chartOfAccountIds }) => {
  try {
    const url = `${BASE_URL}/revenueTypeBudgetAccounts`;
    const response = await api.post(url, { action: 'replace', orgId, locationId, revenueTypeId, chartOfAccountIds });
    return response.data;
  } catch (error) {
    console.error(`Error replacing budget accounts for revenue type ${revenueTypeId}:`, error);
    throw error;
  }
};

// Hotel Market Revenue Types API functions
export const getHotelMarketRevenueTypes = async (orgId, locationId = null) => {
  try {
    const response = await api.get(API_ENDPOINTS.GET_HOTEL_MARKET_REVENUE_TYPES_BY_ORG(orgId, locationId));
    return response.data;
  } catch (error) {
    console.error('Error fetching hotel market revenue types:', error);
    throw error;
  }
};

export const updateHotelMarketRevenueType = async (id, orgId, updateData) => {
  try {
    const url = `${API_ENDPOINTS.GET_HOTEL_MARKET_REVENUE_TYPES}/${id}`;
    const response = await api.put(url, { ...updateData, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error updating hotel market revenue type ${id}:`, error);
    throw error;
  }
};

// Room Stat Types API functions
// includeMappings attaches each type's roomStatMappedTypes rows as `.mappings`.
// Those rows — not the legacy roomStatTypes.mappedType scalar — are what every
// stat rollup reads, so any UI that edits a room stat's mapping needs them.
export const getRoomStatTypes = async (orgId, locationId = null, showDeleted = false, includeMappings = false) => {
  try {
    let url = `${BASE_URL}/roomStatTypes?orgId=${orgId}`;
    if (locationId) {
      url += `&locationId=${locationId}`;
    }
    if (showDeleted) {
      url += `&showDeleted=true`;
    }
    if (includeMappings) {
      url += `&includeMappings=true`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching room stat types:', error);
    throw error;
  }
};

export const updateRoomStatType = async (id, orgId, updateData) => {
  try {
    const url = `${BASE_URL}/roomStatTypes?id=${id}`;
    const response = await api.put(url, { ...updateData, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error updating room stat type ${id}:`, error);
    throw error;
  }
};

export const deleteRoomStatType = async (id) => {
  try {
    const url = `${BASE_URL}/roomStatTypes?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting room stat type ${id}:`, error);
    throw error;
  }
};

export const restoreRoomStatType = async (id, orgId) => {
  try {
    const url = `${BASE_URL}/roomStatTypes?id=${id}`;
    const response = await api.put(url, { isDeleted: false, orgId });
    return response.data;
  } catch (error) {
    console.error(`Error restoring room stat type ${id}:`, error);
    throw error;
  }
};

// Room Stat Mapped Types API functions
export const getRoomStatMappedTypes = async (roomStatTypeId = null) => {
  try {
    let url = `${BASE_URL}/roomStatMappedTypes`;
    if (roomStatTypeId) {
      url += `?roomStatTypeId=${roomStatTypeId}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching room stat mapped types:', error);
    throw error;
  }
};

export const getRoomStatMappedTypeById = async (id) => {
  try {
    const url = `${BASE_URL}/roomStatMappedTypes?id=${id}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching room stat mapped type ${id}:`, error);
    throw error;
  }
};

export const createRoomStatMappedType = async (mappingData) => {
  try {
    const url = `${BASE_URL}/roomStatMappedTypes`;
    const response = await api.post(url, mappingData);
    return response.data;
  } catch (error) {
    console.error('Error creating room stat mapped type:', error);
    throw error;
  }
};

export const updateRoomStatMappedType = async (id, updateData) => {
  try {
    const url = `${BASE_URL}/roomStatMappedTypes?id=${id}`;
    const response = await api.put(url, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error updating room stat mapped type ${id}:`, error);
    throw error;
  }
};

export const deleteRoomStatMappedType = async (id) => {
  try {
    const url = `${BASE_URL}/roomStatMappedTypes?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting room stat mapped type ${id}:`, error);
    throw error;
  }
};

// Replace the full mapped-type set of one room stat type in a single round trip.
// mappedTypes: [{ mappedType, operation }]
export const replaceRoomStatMappedTypes = async ({ roomStatTypeId, orgId, locationId, mappedTypes }) => {
  try {
    const url = `${BASE_URL}/roomStatMappedTypes`;
    const response = await api.post(url, { action: 'replace', roomStatTypeId, orgId, locationId, mappedTypes });
    return response.data;
  } catch (error) {
    console.error(`Error replacing mapped types for room stat type ${roomStatTypeId}:`, error);
    throw error;
  }
};

// The location's mapped-type row for a GL-sourceable stat (Departures, Total
// Guests, …), created on a bare "<Stat> (GL)" stat type when no PMS stat
// carries it — so statistical COA accounts can be mapped to it.
// → { mappedTypeId, roomStatTypeId, created }
export const ensureGlStatMappedType = async ({ orgId, locationId, mappedType }) => {
  try {
    const response = await api.post(`${BASE_URL}/roomStatMappedTypes`, { action: 'ensureGlStat', orgId, locationId, mappedType });
    return response.data;
  } catch (error) {
    console.error(`Error adding GL-sourced stat ${mappedType}:`, error);
    throw error;
  }
};

// Occupancy Forecast Source Mappings — routes the 'OTB'/'Projected' slots per
// location onto normalized forecast types (occupancyForecastMappedTypes)
export const getOccupancyForecastMappings = async (orgId) => {
  try {
    const response = await api.get(`${BASE_URL}/occupancyForecastMappings?orgId=${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching occupancy forecast mappings:', error);
    throw error;
  }
};

export const saveOccupancyForecastMapping = async ({ locationId, slot, typeValue }) => {
  try {
    const response = await api.put(`${BASE_URL}/occupancyForecastMappings`, { locationId, slot, typeValue });
    return response.data;
  } catch (error) {
    console.error('Error saving occupancy forecast mapping:', error);
    throw error;
  }
};

export const resetOccupancyForecastMapping = async (locationId, slot) => {
  try {
    const response = await api.delete(`${BASE_URL}/occupancyForecastMappings?locationId=${locationId}&slot=${slot}`);
    return response.data;
  } catch (error) {
    console.error('Error resetting occupancy forecast mapping:', error);
    throw error;
  }
};

// Bulk-apply one source to a slot on many locations in one call:
// PUT { locationIds:[…], slot, typeValue } → { success, updated?, refreshQueued }
// Market segments (e.g. Lighthouse) -> the location's segment (Occupied Rooms
// stat) accounts. GET returns { segments, accounts } for one location.
export const getOccupancyMarketSegments = async (locationId) => {
  try {
    const response = await api.get(`${BASE_URL}/occupancyForecastMappings/segments?locationId=${locationId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching market segment mappings:', error);
    throw error;
  }
};

// Jev-backed suggestions for the unmapped segments (name matches when Jev is unavailable):
// → { source: 'jev'|'name', suggestions: [{ segmentId, chartOfAccountId, probability }], note }
export const suggestOccupancyMarketSegments = async (locationId) => {
  try {
    const response = await api.post(`${BASE_URL}/occupancyForecastMappings/segments/suggest`, { locationId });
    return response.data;
  } catch (error) {
    console.error('Error fetching market segment suggestions:', error);
    throw error;
  }
};

// mappings: [{ segmentId, chartOfAccountId|null }] — null clears (inherit from parent).
export const saveOccupancyMarketSegments = async ({ locationId, mappings }) => {
  try {
    const response = await api.put(`${BASE_URL}/occupancyForecastMappings/segments`, { locationId, mappings });
    return response.data;
  } catch (error) {
    console.error('Error saving market segment mappings:', error);
    throw error;
  }
};

export const saveOccupancyForecastMappingBulk = async ({ locationIds, slot, typeValue }) => {
  try {
    const response = await api.put(`${BASE_URL}/occupancyForecastMappings`, { locationIds, slot, typeValue });
    return response.data;
  } catch (error) {
    console.error('Error bulk saving occupancy forecast mappings:', error);
    throw error;
  }
};

// Room Stat Account Mappings API functions
// Maps Chart of Account statistical accounts to room stat types for occupancy sourcing
export const getRoomStatAccountMappings = async (orgId, locationId) => {
  try {
    const url = `${BASE_URL}/roomStatAccountMappings?orgId=${orgId}&locationId=${locationId}`;
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching room stat account mappings:', error);
    throw error;
  }
};

export const createRoomStatAccountMapping = async (mappingData) => {
  try {
    const url = `${BASE_URL}/roomStatAccountMappings`;
    const response = await api.post(url, mappingData);
    return response.data;
  } catch (error) {
    console.error('Error creating room stat account mapping:', error);
    throw error;
  }
};

export const updateRoomStatAccountMapping = async (id, updateData) => {
  try {
    const url = `${BASE_URL}/roomStatAccountMappings?id=${id}`;
    const response = await api.put(url, updateData);
    return response.data;
  } catch (error) {
    console.error(`Error updating room stat account mapping ${id}:`, error);
    throw error;
  }
};

export const deleteRoomStatAccountMapping = async (id) => {
  try {
    const url = `${BASE_URL}/roomStatAccountMappings?id=${id}`;
    const response = await api.delete(url);
    return response.data;
  } catch (error) {
    console.error(`Error deleting room stat account mapping ${id}:`, error);
    throw error;
  }
};

// Replace the full COA set of one room stat mapped type in a single round trip.
// mappings: [{ chartOfAccountId, operation }]
export const replaceRoomStatAccountMappings = async ({ roomStatMappedTypeId, orgId, locationId, mappings }) => {
  try {
    const url = `${BASE_URL}/roomStatAccountMappings`;
    const response = await api.post(url, { action: 'replace', roomStatMappedTypeId, orgId, locationId, mappings });
    return response.data;
  } catch (error) {
    console.error(`Error replacing account mappings for room stat mapped type ${roomStatMappedTypeId}:`, error);
    throw error;
  }
};

export const getReportSubscriptionsByOrg = async (orgId, userId) => {
  try {
    // Use orgId and userId as query parameters
    let url = `${API_ENDPOINTS.REPORT_SUBSCRIPTIONS}?orgId=${orgId}`;
    if (userId) {
      url += `&userId=${userId}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching report subscriptions for orgId ${orgId}:`, error);
    throw error;
  }
};

export const getReportSubscriptionsByUser = async (userId, orgId) => {
  try {
    // Use userId and orgId as query parameters  
    let url = `${API_ENDPOINTS.REPORT_SUBSCRIPTIONS}?userId=${userId}`;
    if (orgId) {
      url += `&orgId=${orgId}`;
    }
    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error(`Error fetching report subscriptions for userId ${userId}:`, error);
    throw error;
  }
};

export const getReportSubscription = async (id) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.REPORT_SUBSCRIPTIONS}?id=${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching report subscription ${id}:`, error);
    throw error;
  }
};

export const createReportSubscription = async (subscriptionData) => {
  try {
    // Ensure required fields are present
    if (!subscriptionData.userId || !subscriptionData.reportId || !subscriptionData.orgId) {
      throw new Error('Missing required fields: userId, reportId, and orgId are required');
    }

    // Set default values for new subscriptions
    const subscriptionPayload = {
      ...subscriptionData,
      isActive: subscriptionData.isActive !== undefined ? subscriptionData.isActive : true,
      isDeleted: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: subscriptionData.userId,
      executionHistory: {
        totalExecutions: 0,
        successfulExecutions: 0,
        failedExecutions: 0,
        consecutiveFailures: 0,
        maxConsecutiveFailures: 3
      }
    };

    // Calculate next scheduled execution if not provided
    if (!subscriptionPayload.nextScheduled && subscriptionPayload.triggerTime) {
      // You would need to import and use the schedule calculation logic here
      // For now, we'll set it to be calculated by the backend
      subscriptionPayload.calculateNextScheduled = true;
    }

    const response = await api.post(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, subscriptionPayload);

    return response.data;
  } catch (error) {
    console.error('Error creating report subscription:', error);
    throw error;
  }
};

export const updateReportSubscription = async (id, subscriptionData) => {
  try {
    const updatePayload = {
      ...subscriptionData,
      id: id,
      updatedAt: new Date().toISOString()
    };

    // Calculate next scheduled execution if trigger time changed
    if (subscriptionData.triggerTime) {
      updatePayload.calculateNextScheduled = true;
    }

    const response = await api.patch(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, updatePayload);

    return response.data;
  } catch (error) {
    console.error(`Error updating report subscription ${id}:`, error);
    throw error;
  }
};

export const deleteReportSubscription = async (id) => {
  try {
    const deletePayload = {
      id: id,
      isDeleted: true,
      isActive: false,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const response = await api.patch(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, deletePayload);

    return response.data;
  } catch (error) {
    console.error(`Error deleting report subscription ${id}:`, error);
    throw error;
  }
};

export const toggleReportSubscriptionStatus = async (id, isActive) => {
  try {
    const updatePayload = {
      id: id,
      isActive: isActive,
      updatedAt: new Date().toISOString()
    };

    // If activating, recalculate next scheduled time
    if (isActive) {
      updatePayload.calculateNextScheduled = true;
    }

    const response = await api.patch(API_ENDPOINTS.REPORT_SUBSCRIPTIONS, updatePayload);

    return response.data;
  } catch (error) {
    console.error(`Error toggling report subscription ${id} status:`, error);
    throw error;
  }
};

export const testReportSubscription = async (id) => {
  try {
    const response = await api.post(API_ENDPOINTS.TEST_REPORT_SUBSCRIPTION(id), {});

    return response.data;
  } catch (error) {
    console.error(`Error testing report subscription ${id}:`, error);
    throw error;
  }
};

// Renders the native PDF book for a nativePages subscription right now and
// emails it to the caller — same endpoint the report-page schedule modal uses.
export const testNativePagesReportSubscription = async (id) => {
  try {
    const response = await api.post(API_ENDPOINTS.NATIVE_REPORT_SUBSCRIPTION_TEST(id));
    return response.data;
  } catch (error) {
    console.error(`Error testing nativePages report subscription ${id}:`, error);
    throw error;
  }
};

export const validateReportSubscription = (subscriptionData) => {
  const errors = [];
  
  // Check top-level required fields
  if (!subscriptionData.reportId) {
    errors.push('reportId is required');
  }
  if (!subscriptionData.workspaceId) {
    errors.push('workspaceId is required');
  }
  
  // Check nested required fields
  if (!subscriptionData.triggerTime?.frequency) {
    errors.push('frequency is required');
  }
  
  if (!subscriptionData.deliveryConfig?.emailConfig?.attachmentFormat) {
    errors.push('format is required');
  }
  
  // Check frequency-specific requirements
  if (subscriptionData.triggerTime?.frequency === 'weekly' && 
      (!subscriptionData.triggerTime?.days || subscriptionData.triggerTime.days.length === 0)) {
    errors.push('At least one day must be selected for weekly frequency');
  }
  
  if (subscriptionData.triggerTime?.frequency === 'monthly' && 
      (!subscriptionData.triggerTime?.days || subscriptionData.triggerTime.days.length === 0)) {
    errors.push('At least one day must be selected for monthly frequency');
  }
  
  // Check if times are provided
  if (!subscriptionData.triggerTime?.times || subscriptionData.triggerTime.times.length === 0) {
    errors.push('At least one time must be specified');
  }
  
  return { 
    isValid: errors.length === 0, 
    errors 
  };
};

// Location Comp Set API functions
export const getLocationCompSets = async (orgId, locationId = null) => {
  try {
    if (!orgId) {
      throw new Error('orgId is required to fetch location comp sets');
    }
    
    let url;
    if (locationId) {
      url = API_ENDPOINTS.GET_LOCATION_COMP_SETS_BY_ORG_LOCATION(orgId, locationId);
    } else {
      url = API_ENDPOINTS.GET_LOCATION_COMP_SETS_BY_ORG(orgId);
    }
    

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching location comp sets:', error);
    throw error;
  }
};

export const getLocationCompSetById = async (id) => {
  try {
    if (!id) {
      throw new Error('id is required to fetch location comp set');
    }
    
    const url = API_ENDPOINTS.GET_LOCATION_COMP_SET_BY_ID(id);

    const response = await api.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching location comp set by id:', error);
    throw error;
  }
};

export const createLocationCompSet = async (compSetData) => {
  try {
    if (!compSetData.orgId || !compSetData.locationId) {
      throw new Error('orgId and locationId are required to create location comp set');
    }
    

    const response = await api.post(API_ENDPOINTS.LOCATION_COMP_SETS, compSetData);
    return response.data;
  } catch (error) {
    console.error('Error creating location comp set:', error);
    throw error;
  }
};

export const updateLocationCompSet = async (compSetData) => {
  try {
    if (!compSetData.id) {
      throw new Error('id is required to update location comp set');
    }
    

    const response = await api.put(API_ENDPOINTS.LOCATION_COMP_SETS, compSetData);
    return response.data;
  } catch (error) {
    console.error('Error updating location comp set:', error);
    throw error;
  }
};

export const deleteLocationCompSet = async (id) => {
  try {
    if (!id) {
      throw new Error('id is required to delete location comp set');
    }
    

    const response = await api.delete(`${API_ENDPOINTS.LOCATION_COMP_SETS}?id=${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting location comp set:', error);
    throw error;
  }
};

// File Upload ETL API function
// Fetch ETL configuration for a hotel to get available file types
export const getEtlConfigForHotel = async (hotelId) => {
  try {
    if (!hotelId) {
      throw new Error('Hotel ID is required');
    }

    // Create a simple endpoint to fetch ETL config
    const response = await api.get(`${BASE_URL}/etlConfig?hotelId=${hotelId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL config:', error);
    throw error;
  }
};

// ETL configurations catalog — one flattened row per (hotel, fileConfig) with
// match keywords, method, extraction service, and the latest processing-state
// outcome. Powers the File Tracking "Configurations" view.
export const getEtlConfigCatalog = async (hotelIds) => {
  try {
    if (!hotelIds || !Array.isArray(hotelIds) || hotelIds.length === 0) {
      throw new Error('Hotel IDs array is required');
    }
    const response = await api.get(`${BASE_URL}/etlConfig?hotelIds=${hotelIds.join(',')}&catalog=true`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL config catalog:', error);
    throw error;
  }
};

// File Tracking API functions
// Fetch ETL configurations with tracking info for multiple hotels
export const getEtlConfigsForHotels = async (hotelIds) => {
  try {
    if (!hotelIds || !Array.isArray(hotelIds) || hotelIds.length === 0) {
      throw new Error('Hotel IDs array is required');
    }

    const response = await api.get(`${BASE_URL}/etlConfig?hotelIds=${hotelIds.join(',')}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL configs for hotels:', error);
    throw error;
  }
};

// Update tracking configuration for a specific file type
/**
 * The missing-file alert time a feed learns from its own arrivals (weekday and
 * weekend), with the numbers behind it. See azfApi/utils/learnedAlertTime.js.
 */
export const getLearnedAlertTime = async (hotelId, fileType) => {
  const response = await api.get(`${BASE_URL}/etlConfig/learnedAlertTime`, { params: { hotelId, fileType } });
  return response.data;
};

export const updateFileTrackingConfig = async (hotelId, fileType, trackingConfig) => {
  try {
    if (!hotelId || !fileType) {
      throw new Error('Hotel ID and file type are required');
    }

    const response = await api.put(`${BASE_URL}/etlConfig`, {
      hotelId,
      fileType,
      tracking: trackingConfig
    });
    return response.data;
  } catch (error) {
    console.error('Error updating file tracking config:', error);
    throw error;
  }
};

// Clear manual review flag for a specific file type
export const clearManualReviewFlag = async (hotelId, fileType, clearedBy) => {
  try {
    if (!hotelId || !fileType) {
      throw new Error('Hotel ID and file type are required');
    }

    const response = await api.put(`${BASE_URL}/etlConfig`, {
      hotelId,
      fileType,
      tracking: {
        manualReviewRequired: false,
        manualReviewCleared: true,
        manualReviewClearedAt: new Date().toISOString(),
        manualReviewClearedBy: clearedBy
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error clearing manual review flag:', error);
    throw error;
  }
};

// Fetch historical file tracking records
// hotelIds is the primary query method - orgId is optional
export const getFileTrackingHistory = async (orgId, options = {}) => {
  try {
    // Need either orgId or hotelIds
    if (!orgId && (!options.hotelIds || options.hotelIds.length === 0)) {
      throw new Error('Either Organization ID or Hotel IDs are required');
    }

    const params = new URLSearchParams();

    // hotelIds is the primary query method (more reliable than orgId)
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    // orgId is optional fallback
    if (orgId) {
      params.append('orgId', orgId);
    }
    if (options.fileType) {
      params.append('fileType', options.fileType);
    }
    if (options.startDate) {
      params.append('startDate', options.startDate);
    }
    if (options.endDate) {
      params.append('endDate', options.endDate);
    }
    if (options.status) {
      params.append('status', options.status);
    }

    const response = await api.get(`${BASE_URL}/fileTrackingHistory?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching file tracking history:', error);
    throw error;
  }
};

// Fetch the latest state per (hotelId, fileType) for the Current Status view.
// Uses a dedicated SQL query (ROW_NUMBER PARTITION BY) instead of downloading
// the full 60-day history and deduping client-side.
export const getFileTrackingCurrentStatus = async (orgId, options = {}) => {
  try {
    if (!orgId && (!options.hotelIds || options.hotelIds.length === 0)) {
      throw new Error('Either Organization ID or Hotel IDs are required');
    }
    const params = new URLSearchParams();
    params.append('view', 'current');
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    if (orgId) {
      params.append('orgId', orgId);
    }
    const response = await api.get(`${BASE_URL}/fileTrackingHistory?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching file tracking current status:', error);
    throw error;
  }
};

// ----- Document catalog (unmatched email attachments) ----------------------
// Files emailed to reports_{hotelId}@lodgeic.io that matched no ETL config —
// captured, auto-classified, and archived by azfEtl's document capture service.

export const getEtlDocuments = async (options = {}) => {
  try {
    if (!options.hotelIds || options.hotelIds.length === 0) {
      throw new Error('Hotel IDs are required');
    }
    const params = new URLSearchParams();
    params.append('hotelIds', options.hotelIds.join(','));
    if (options.category) params.append('category', options.category);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.signedOff !== undefined) params.append('signedOff', String(options.signedOff));
    // Server-side search + paging. The catalog is a union of two large tables,
    // so filtering in the browser would only ever search the page it had.
    if (options.search) params.append('search', options.search);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.offset) params.append('offset', String(options.offset));
    const response = await api.get(`${BASE_URL}/etlDocuments?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching document catalog:', error);
    throw error;
  }
};

// updates: { category?, displayName?, signOff?: true, updatedBy }
// Recategorizing also updates the classification rule so the correction
// applies to every future arrival of the same filename pattern.
// Runs the azfEtl inbox sweep now (lodgeic-api holds the function key).
// Resolves with { processedHotelIds, hotelsWithFileTypes } scoped to hotelIds.
export const checkEtlInbox = async (hotelIds) => {
  try {
    if (!hotelIds || hotelIds.length === 0) throw new Error('Hotel IDs are required');
    const response = await api.post(`${BASE_URL}/etlDocuments/checkInbox`, { hotelIds }, { timeout: 5 * 60 * 1000 });
    return response.data;
  } catch (error) {
    console.error('Error checking ETL inbox:', error);
    throw error;
  }
};

export const updateEtlDocument = async (id, updates) => {
  try {
    if (!id) throw new Error('Document ID is required');
    const response = await api.put(`${BASE_URL}/etlDocuments`, { id, ...updates });
    return response.data;
  } catch (error) {
    console.error('Error updating document:', error);
    throw error;
  }
};

// ----- ETL DLQ (Phase 4 of email-etl-rewrite) -----------------------------
// These call the etlDlq Azure Function in azfApi/etlDlq. They power the DLQ
// section of the FileTracking page. See docs/email-etl-scaling-rewrite.md.

// Fetch a paginated list of state rows for the DLQ view. Filters are passed
// as query params. Returns { total, limit, offset, rows: [...] }.
export const getEtlDlqList = async (orgId, options = {}) => {
  try {
    // No scope guard here any more: the server derives the caller's accessible
    // organizations and locations and intersects them with whatever is passed.
    // `hotelIds` is a narrowing filter (usually one location), never the thing
    // that establishes tenancy — enumerating all of them produced a 1kB URL and
    // meant the browser was the only thing keeping the query scoped.
    const params = new URLSearchParams();
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    if (orgId) params.append('orgId', orgId);
    if (options.status) params.append('status', options.status);          // single or comma-separated
    if (options.fileType) params.append('fileType', options.fileType);
    if (options.category) params.append('category', options.category);
    if (options.startDate) params.append('startDate', options.startDate);
    if (options.endDate) params.append('endDate', options.endDate);
    if (options.limit) params.append('limit', String(options.limit));
    if (options.offset) params.append('offset', String(options.offset));
    // includeMissed — when true, surface missed-file sentinel rows (category
    // MISSED_FILE) inline with real DLQ entries. Defaults to false because
    // missed files are already surfaced in the Historical View via the
    // unified fileTrackingHistory endpoint.
    if (options.includeMissed === true) params.append('includeMissed', 'true');
    // withIssue — only rows with an issue category or a last error.
    if (options.withIssue === true) params.append('withIssue', 'true');
    // updatedWithinHours — only rows whose last transition is this recent.
    if (options.updatedWithinHours) params.append('updatedWithinHours', String(options.updatedWithinHours));

    const response = await api.get(`${BASE_URL}/etlDlq?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL DLQ list:', error);
    throw error;
  }
};

// Fetch a single state row plus its event log and audit-action history.
// Returns { state, events, actions }.
export const getEtlDlqDetail = async (id) => {
  try {
    if (!id) throw new Error('id is required');
    const params = new URLSearchParams();
    params.append('id', String(id));
    const response = await api.get(`${BASE_URL}/etlDlq/detail?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL DLQ detail:', error);
    throw error;
  }
};

// Fetch aggregate stats for the DLQ dashboard. Returns
//   { counts, byCategory, topHotelsByDlq }.
export const getEtlDlqStats = async (orgId, options = {}) => {
  try {
    // Scope comes from the caller's own access — see getEtlDlqList.
    const params = new URLSearchParams();
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    if (orgId) params.append('orgId', orgId);
    const response = await api.get(`${BASE_URL}/etlDlq/stats?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL DLQ stats:', error);
    throw error;
  }
};

// Fetch ingestion-to-normalized lag percentiles for the SLO dashboard. Returns
//   { windowHours, overall: { sampleSize, p50Seconds, p95Seconds, p99Seconds, ... }, perHotel: [...] }.
export const getEtlDlqLag = async (orgId, options = {}) => {
  try {
    // Scope comes from the caller's own access — see getEtlDlqList.
    const params = new URLSearchParams();
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    if (orgId) params.append('orgId', orgId);
    if (options.windowHours) params.append('windowHours', String(options.windowHours));
    const response = await api.get(`${BASE_URL}/etlDlq/lag?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching ETL DLQ lag:', error);
    throw error;
  }
};

// Perform a resolution action on a DLQ entry. Action types:
//   'replay'              — re-run the file from the appropriate stage
//   'mark_resolved'       — manually resolve without replay (requires note)
//   'reassign_type'       — change fileType + replay (requires newFileType)
//   'escalate'            — flag for engineering review (requires note)
//   'replay_from_stage'   — engineer-only: replay from a specified stage
//   'bulk_replay'         — engineer-only: batch replay by stateIds or filter
//   'edit_state'          — engineer-only: direct state mutation
//   'refresh_dashboard_fact' — refresh derived dashboard fact rows for state dates
export const postEtlDlqAction = async (action, payload = {}) => {
  try {
    if (!action) throw new Error('action is required');
    const body = { action, ...payload };
    const response = await api.post(`${BASE_URL}/etlDlq/action`, body);
    return response.data;
  } catch (error) {
    console.error(`Error performing ETL DLQ action '${action}':`, error);
    throw error;
  }
};

// Replay queue (Issues tab bulk replay). Queuing returns at once; a worker on
// the API replays the files in the background, one at a time per hotel.
//   queueEtlDlqReplays({ stateIds, notes }) → { queued, jobs: [{ id, stateId }], skipped: [{ stateId, reason }] }
//   getEtlDlqReplayQueue()                  → { available, jobs: [...] } (active + finished in the last few hours)
//   cancelEtlDlqReplays(jobIds)             → { cancelled }
export const queueEtlDlqReplays = async ({ stateIds, notes, performedByRole } = {}) => {
  const response = await api.post(`${BASE_URL}/etlDlq/replayQueue`, { stateIds, notes, performedByRole });
  return response.data;
};

export const getEtlDlqReplayQueue = async (orgId, options = {}) => {
  const params = new URLSearchParams();
  if (orgId) params.append('orgId', orgId);
  if (options.sinceHours) params.append('sinceHours', String(options.sinceHours));
  const response = await api.get(`${BASE_URL}/etlDlq/replayQueue?${params.toString()}`);
  return response.data;
};

export const cancelEtlDlqReplays = async (jobIds, performedByRole) => {
  const response = await api.post(`${BASE_URL}/etlDlq/replayQueue/cancel`, { jobIds, performedByRole });
  return response.data;
};

// ----- Unmapped revenue codes (Unmapped Codes tab on FileTracking) ----------
// These call the unmappedRevenueCodes Azure Function. Unmapped codes are
// revenueTypes rows with a blank mappedType — their hotelRevenue dollars are
// excluded from every report until someone maps or ignores them.

// Fetch unmapped codes with dollarized impact + portfolio-precedent
// suggestions. Returns { activityDays, rows, knownMappedTypesByLocation, typeClassifications }.
export const getUnmappedRevenueCodes = async (orgId, options = {}) => {
  try {
    if (!orgId && (!options.hotelIds || options.hotelIds.length === 0)) {
      throw new Error('Either Organization ID or Hotel IDs are required');
    }
    const params = new URLSearchParams();
    if (options.hotelIds && options.hotelIds.length > 0) {
      params.append('hotelIds', options.hotelIds.join(','));
    }
    if (orgId) params.append('orgId', orgId);
    if (options.activityDays) params.append('activityDays', String(options.activityDays));
    const response = await api.get(`${BASE_URL}/unmappedRevenueCodes?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching unmapped revenue codes:', error);
    throw error;
  }
};

// Apply a triage decision to one or more codes. action:
//   'map'               — set mappedType (+ optional typeClassificationId)
//   'accept_suggestion' — same, but audited as an accepted precedent suggestion
//   'ignore'            — set mappedType='DNI' (durable exclude-forward)
// items: [{ revenueTypeId, mappedType?, typeClassificationId?, suggestionConfidence? }]
export const postUnmappedRevenueAction = async (action, items, notes = null) => {
  try {
    if (!action) throw new Error('action is required');
    if (!items || items.length === 0) throw new Error('items is required');
    const response = await api.post(`${BASE_URL}/unmappedRevenueCodes/action`, { action, items, notes });
    return response.data;
  } catch (error) {
    console.error(`Error performing unmapped revenue action '${action}':`, error);
    throw error;
  }
};

/**
 * Fetch a blob URL through the backend proxy (pbiPdfProxy) and return a local
 * object URL. Works for any file type — PDF, CSV, JSON, TXT, etc. The proxy
 * appends the SAS token server-side so the frontend never needs storage creds.
 */
export const fetchBlobViaProxy = async (blobUrl, mimeType = null) => {
  // DEMO org: PDFs always resolve to the redacted sample night audit — same
  // override the PBI link → PDF viewer applies in fetchPdfFromBlob.
  if (isDemoOrg() && String(blobUrl).split('?')[0].toLowerCase().endsWith('.pdf')) {
    blobUrl = DEMO_REDACTED_PDF_URL;
    mimeType = 'application/pdf';
  }
  try {
    const decodedUrl = new DOMParser().parseFromString(blobUrl, 'text/html').documentElement.textContent;
    const response = await api.post(API_ENDPOINTS.GET_PDF_PROXY, {
      url: decodedUrl,
    }, {
      responseType: 'blob',
      timeout: 30000,
    });
    if (!response.data) throw new Error('No data received from proxy');
    let blob = response.data;
    if (mimeType && blob.type !== mimeType) {
      blob = blob.slice(0, blob.size, mimeType);
    }
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching blob via proxy:', error);
    throw error;
  }
};

export const getEtlDlqArchiveFiles = async (stateId) => {
  try {
    const response = await api.get(`${BASE_URL}/etlDlq/archiveFiles`, {
      params: { id: stateId },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching archive files for state ${stateId}:`, error);
    throw error;
  }
};

export const getEtlDlqGeneratePrompt = async (hotelId, fileType) => {
  try {
    const response = await api.get(`${BASE_URL}/etlDlq/generatePrompt`, {
      params: { hotelId, fileType },
    });
    return response.data;
  } catch (error) {
    console.error(`Error generating AI prompt for hotel ${hotelId} fileType ${fileType}:`, error);
    throw error;
  }
};

// Update a historical file tracking record
export const updateFileTrackingHistoryRecord = async (id, orgId, updates) => {
  try {
    if (!id || !orgId) {
      throw new Error('Record ID and Organization ID are required');
    }

    const response = await api.put(`${BASE_URL}/fileTrackingHistory`, {
      id,
      orgId,
      ...updates
    });
    return response.data;
  } catch (error) {
    console.error('Error updating file tracking history record:', error);
    throw error;
  }
};

// Search users for file tracking email with role/location filtering
export const searchUsersForFileTracking = async (options = {}) => {
  try {
    const params = new URLSearchParams();

    if (options.search) params.append('search', options.search);
    if (options.role) params.append('role', options.role);
    if (options.locationId) params.append('locationId', options.locationId);
    if (options.orgCode) params.append('orgCode', options.orgCode);
    if (options.jobTitle) params.append('jobTitle', options.jobTitle);
    if (options.hotelId) params.append('hotelId', options.hotelId);
    if (options.fileType) params.append('fileType', options.fileType);

    const response = await api.get(`${BASE_URL}/fileTrackingEmail?${params.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error searching users for file tracking:', error);
    throw error;
  }
};

// Send file tracking email
export const sendFileTrackingEmail = async ({ recipients, subject, message, records, effectiveDate }) => {
  try {
    if (!recipients || recipients.length === 0) {
      throw new Error('At least one recipient is required');
    }
    if (!subject) {
      throw new Error('Subject is required');
    }

    const response = await api.post(`${BASE_URL}/fileTrackingEmail`, {
      recipients,
      subject,
      message,
      records,
      effectiveDate
    });
    return response.data;
  } catch (error) {
    console.error('Error sending file tracking email:', error);
    throw error;
  }
};

export const uploadFileForEtl = async (hotelId, file, fileType = 'audit', businessDate = null, extra = null) => {
  try {
    if (!hotelId || !file) {
      throw new Error('Hotel ID and file are required');
    }

    // Look up OCR service preference from schema configuration. When the
    // schema config doesn't declare one, LEAVE IT UNSET — the backend resolves
    // the correct service from the hotel's etl-config fileConfig (important
    // for aiExtraction/textract hotels where hardcoding 'formRecognizer'
    // routed files down the wrong OCR path).
    let ocrService = null;
    try {
      const schemaConfig = await getSchemaConfig(hotelId, fileType);
      if (schemaConfig && schemaConfig.success && schemaConfig.data && schemaConfig.data.ocrService) {
        ocrService = schemaConfig.data.ocrService;
      }
    } catch (configError) {
      console.warn(`Failed to fetch OCR service preference for hotel ${hotelId}:`, configError.message);
    }

    // Convert file to base64
    const fileBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (data:application/pdf;base64, etc.)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload = {
      hotelId: hotelId,
      fileName: file.name,
      fileData: fileBase64,
      fileType: fileType,
      ...(ocrService && { ocrService: ocrService }), // Only when schema config declares one
      ...(businessDate && { businessDate: businessDate }), // Only include if provided
      ...(extra && typeof extra === 'object' ? extra : {}) // e.g. { medalliaSiteId } for survey exports that span properties
    };

    const logMessage = businessDate 
      ? `Uploading file ${file.name} for hotel ${hotelId} with file type ${fileType}, OCR service ${ocrService}, and business date ${businessDate} via ETL endpoint`
      : `Uploading file ${file.name} for hotel ${hotelId} with file type ${fileType} and OCR service ${ocrService} via ETL endpoint`;

    const response = await api.post(API_ENDPOINTS.FILE_UPLOAD_ETL, payload);
    

    return response.data;
  } catch (error) {
    console.error('Error uploading file for ETL:', error);
    throw error;
  }
};

// HIA accounting GL import — uploads an HIA "Journal Transactions for Period"
// xlsx export. The property is resolved server-side from the file's own
// Company/Branch header (Location.accountingId), and the import is idempotent
// per period month (delete-and-replace). Pass dryRun=true to validate only.
export const uploadHiaGlFile = async (file, dryRun = false, allowUnbalanced = false) => {
  try {
    const fileBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const response = await api.post(API_ENDPOINTS.HIA_GL_IMPORT, {
      fileName: file.name,
      fileData: fileBase64,
      ...(dryRun && { dryRun: true }),
      ...(allowUnbalanced && { allowUnbalanced: true }),
    });
    return response.data;
  } catch (error) {
    console.error('Error importing HIA GL file:', error);
    throw error;
  }
};

// Helper function to parse tables from text content
const parseTablesFromText = (textContent) => {
  try {
    const tables = [];
    const lines = textContent.split('\n');
    let currentTable = null;
    let tableId = 1;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (!line) {
        if (currentTable && currentTable.data.length > 0) {
          // End current table if we hit empty lines
          tables.push(currentTable);
          currentTable = null;
        }
        continue;
      }
      
      // Detect table separator lines (lines with dashes, equals, or underscores)
      if (/^[-=_\s]+$/.test(line)) {
        continue; // Skip separator lines
      }
      
      // Try to detect if this line looks like tabular data
      // Look for lines with multiple columns separated by spaces
      const columns = line.split(/\s{2,}|\t/).filter(col => col.trim());
      
      // If we have multiple columns, this might be a table
      if (columns.length >= 2) {
        if (!currentTable) {
          // Start a new table
          currentTable = {
            id: tableId++,
            data: []
          };
        }
        
        // Add row to current table
        currentTable.data.push(columns);
      } else if (currentTable && currentTable.data.length > 0) {
        // If we have a single column and a current table, end the table
        tables.push(currentTable);
        currentTable = null;
      }
    }
    
    // Add the last table if it exists
    if (currentTable && currentTable.data.length > 0) {
      tables.push(currentTable);
    }
    

    return tables;
  } catch (error) {
    console.error('Error parsing tables from text:', error);
    return [];
  }
};

// Helper function to extract key-value pairs from text content
const extractKeyValuePairsFromText = (textContent) => {
  try {
    const keyValuePairs = {};
    const lines = textContent.split('\n');
    
    for (const line of lines) {
      // Look for patterns like "Key: Value" or "Key = Value"
      const colonMatch = line.match(/^([^:]+):\s*(.+)$/);
      if (colonMatch) {
        const key = colonMatch[1].trim();
        const value = colonMatch[2].trim();
        if (key && value) {
          keyValuePairs[key] = value;
        }
        continue;
      }
      
      // Look for patterns like "Key = Value"
      const equalsMatch = line.match(/^([^=]+)=\s*(.+)$/);
      if (equalsMatch) {
        const key = equalsMatch[1].trim();
        const value = equalsMatch[2].trim();
        if (key && value) {
          keyValuePairs[key] = value;
        }
        continue;
      }
      
      // Look for common report headers that might contain key info
      // Like "Report Name" or "Date: XX/XX/XXXX"
      if (line.includes('Report') || line.includes('Date') || line.includes('Page')) {
        const spaceMatch = line.match(/^([^:]+?):\s*(.+)$/);
        if (spaceMatch) {
          const key = spaceMatch[1].trim();
          const value = spaceMatch[2].trim();
          if (key && value) {
            keyValuePairs[key] = value;
          }
        }
      }
    }
    

    return keyValuePairs;
  } catch (error) {
    console.error('Error extracting key-value pairs from text:', error);
    return {};
  }
};

// File conversion function for XLSM to XLSX
export const convertFile = async (file, targetFormat = 'xlsx') => {
  try {
    const fileName = file.name;
    const fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.') + 1);

    // Check if conversion is needed
    if (fileExtension === targetFormat) {
      return file; // No conversion needed
    }

    // Convert file to base64
    const fileBase64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload = {
      fileName: fileName,
      fileData: fileBase64,
      targetFormat: targetFormat
    };

    const response = await api.post(API_ENDPOINTS.FILE_CONVERSION, payload);

    if (response.data && response.data.success) {
      // Create a new File object from the converted data
      const convertedData = response.data.convertedFileData;
      const convertedFileName = response.data.convertedFileName;

      // Convert base64 back to blob
      const byteCharacters = atob(convertedData);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create a new File object with the converted name
      const convertedFile = new File([blob], convertedFileName, {
        type: blob.type,
        lastModified: Date.now()
      });

      return convertedFile;
    } else {
      throw new Error(response.data?.error || 'File conversion failed');
    }
  } catch (error) {
    console.error('File conversion error:', error);
    throw error;
  }
};

// Schema Builder API functions
export const processFormRecognizerFile = async (file, fileType, ocrService = 'formRecognizer', selectedPages = null, preprocess = false) => {
  try {
    if (!file || !fileType) {
      throw new Error('File and file type are required');
    }

    let fileName = file.name;
    let fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    let processFile = file;

    // Check if it's an XLSM file that needs conversion
    if (fileExtension === '.xlsm') {

      try {
        processFile = await convertFile(file, 'xlsx');
        fileName = processFile.name;
        fileExtension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));

      } catch (conversionError) {
        console.error('Failed to convert XLSM file:', conversionError);
        throw new Error(`Failed to convert XLSM file: ${conversionError.message}`);
      }
    }

    // .xlsb goes to the backend as-is: its parser reads binary workbooks
    // directly, and converting first would download and re-upload a file
    // that is roughly twice the size.
    if (fileExtension === '.xlsx' || fileExtension === '.xls' || fileExtension === '.xlsb') {
      // Handle Excel files - send to backend for processing

      // Convert file to base64
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(processFile);
      });

      const payload = {
        fileName: fileName,
        fileData: fileBase64,
        fileType: fileType,
        ocrService: 'excel-parser', // Special service for Excel files
        selectedPages: selectedPages
      };

      const response = await api.post(`${BASE_URL}/schemaFormRecognizer`, payload);
      

      // Ensure we return the extracted data in the expected format
      if (response.data && response.data.extractedData) {
        return response.data.extractedData;
      }
      return response.data;
      
    } else if (fileExtension === '.txt') {
      // Handle TXT files directly without Form Recognizer

      // Read text content directly
      const textContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      // Parse tables from text content
      const parsedTables = parseTablesFromText(textContent);
      
      // Extract key-value pairs from text
      const keyValuePairs = extractKeyValuePairsFromText(textContent);

      // Return data in the same format as Form Recognizer for consistency
      return {
        success: true,
        fileName: fileName,
        fileType: fileType,
        tables: parsedTables,
        keyValuePairs: keyValuePairs,
        extractedText: textContent
      };
    } else {
      // Handle PDF files with Form Recognizer

      // Convert file to base64
      const fileBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const payload = {
        fileName: fileName,
        fileData: fileBase64,
        fileType: fileType,
        ocrService: ocrService,
        selectedPages: selectedPages,
        preprocess: preprocess
      };

      const response = await api.post(`${BASE_URL}/schemaFormRecognizer`, payload);
      

      return response.data;
    }
  } catch (error) {
    console.error('Error processing file:', error);
    throw error;
  }
};

export const getSchemaConfig = async (hotelId, fileType) => {
  try {
    if (!hotelId || !fileType) {
      throw new Error('Hotel ID and file type are required');
    }

    const response = await api.get(`${BASE_URL}/schemaConfig?hotelId=${hotelId}&fileType=${fileType}`);
    
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      // Schema doesn't exist yet - this is expected for new schemas
      return null;
    }
    console.error('Error fetching schema config:', error);
    throw error;
  }
};

export const saveSchemaConfig = async (hotelId, fileType, schemaConfig) => {
  try {
    if (!hotelId || !fileType || !schemaConfig) {
      throw new Error('Hotel ID, file type, and schema config are required');
    }

    const payload = {
      hotelId,
      fileType,
      ...schemaConfig,
      updatedAt: new Date().toISOString()
    };

    const response = await api.post(`${BASE_URL}/schemaConfig`, payload);
    

    return response.data;
  } catch (error) {
    console.error('Error saving schema config:', error);
    throw error;
  }
};

// Hotel-level zip bundle configuration. A zip attachment is only unpacked by
// the ETL if the zip's filename matches one of the hotel's approved bundles.
export const getZipBundles = async (hotelId) => {
  try {
    if (!hotelId) throw new Error('Hotel ID is required');
    const response = await api.get(`${BASE_URL}/zipBundles?hotelId=${hotelId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching zip bundles:', error);
    throw error;
  }
};

export const saveZipBundles = async (hotelId, zipBundles) => {
  try {
    if (!hotelId) throw new Error('Hotel ID is required');
    const response = await api.put(`${BASE_URL}/zipBundles`, { hotelId, zipBundles });
    return response.data;
  } catch (error) {
    console.error('Error saving zip bundles:', error);
    throw error;
  }
};

export const deleteSchemaConfig = async (hotelId, fileType) => {
  try {
    if (!hotelId || !fileType) {
      throw new Error('Hotel ID and file type are required');
    }

    const response = await api.delete(`${BASE_URL}/schemaConfig?hotelId=${hotelId}&fileType=${fileType}`);
    
    return response.data;
  } catch (error) {
    console.error('Error deleting schema config:', error);
    throw error;
  }
};

export const testSchemaMapping = async (hotelId, fileType, schemaConfig, extractedData) => {
  try {
    if (!hotelId || !fileType || !schemaConfig || !extractedData) {
      throw new Error('Hotel ID, file type, schema config, and extracted data are required');
    }

    const payload = {
      hotelId,
      fileType,
      schemaConfig,
      extractedData
    };

    const response = await api.post(`${BASE_URL}/schemaTest`, payload);
    

    return response.data;
  } catch (error) {
    console.error('Error testing schema mapping:', error);
    throw error;
  }
};

// Hotel Revenue API functions
export const getHotelRevenue = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    
    const response = await api.get(`${BASE_URL}/hotelRevenue?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hotel revenue:', error);
    throw error;
  }
};

export const getHotelRevenueById = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/hotelRevenue/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hotel revenue by ID:', error);
    throw error;
  }
};

export const createHotelRevenue = async (data) => {
  try {
    const response = await api.post(`${BASE_URL}/hotelRevenue`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating hotel revenue:', error);
    throw error;
  }
};

export const updateHotelRevenue = async (id, data) => {
  try {
    const response = await api.put(`${BASE_URL}/hotelRevenue/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating hotel revenue:', error);
    throw error;
  }
};

export const deleteHotelRevenue = async (id) => {
  try {
    const response = await api.delete(`${BASE_URL}/hotelRevenue/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting hotel revenue:', error);
    throw error;
  }
};

// Hotel Room Stats API functions
export const getHotelRoomStats = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    
    const response = await api.get(`${BASE_URL}/hotelRoomStats?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hotel room stats:', error);
    throw error;
  }
};

export const getHotelRoomStatsById = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/hotelRoomStats/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching hotel room stats by ID:', error);
    throw error;
  }
};

export const createHotelRoomStats = async (data) => {
  try {
    const response = await api.post(`${BASE_URL}/hotelRoomStats`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating hotel room stats:', error);
    throw error;
  }
};

export const updateHotelRoomStats = async (id, data) => {
  try {
    const response = await api.put(`${BASE_URL}/hotelRoomStats/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating hotel room stats:', error);
    throw error;
  }
};

export const deleteHotelRoomStats = async (id) => {
  try {
    const response = await api.delete(`${BASE_URL}/hotelRoomStats/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting hotel room stats:', error);
    throw error;
  }
};

// Imports API functions
export const getImports = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        queryParams.append(key, params[key]);
      }
    });
    
    const response = await api.get(`${BASE_URL}/imports?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching imports:', error);
    throw error;
  }
};

export const getImportById = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/imports/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching import by ID:', error);
    throw error;
  }
};

export const createImport = async (data) => {
  try {
    const response = await api.post(`${BASE_URL}/imports`, data);
    return response.data;
  } catch (error) {
    console.error('Error creating import:', error);
    throw error;
  }
};

export const updateImport = async (id, data) => {
  try {
    const response = await api.put(`${BASE_URL}/imports/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating import:', error);
    throw error;
  }
};

export const deleteImport = async (id) => {
  try {
    const response = await api.delete(`${BASE_URL}/imports/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting import:', error);
    throw error;
  }
};

// Fetch PDF from nightAuditPath (for import review)
export const fetchImportPdf = async (filePath) => {
  try {
    // If it's already a blob URL, use it directly
    if (filePath.startsWith('blob:')) {
      return filePath;
    }

    

    // Use the dedicated import PDF proxy endpoint
    const response = await api.get(`${BASE_URL}/importPdfProxy`, {
      params: { url: filePath },
      // Important: We request blob so we can handle PDF or text dynamically
      responseType: 'blob' 
    });
    

    if (!response.data) {
      throw new Error('No data received from import proxy');
    }

    const contentType = response.headers['content-type'];
    
    const requestedJsonFile = String(filePath).split('?')[0].split('#')[0].toLowerCase().endsWith('.json');

    // Check if we received a JSON error response instead of file data. JSON
    // source files are valid viewer inputs, so only treat application/json as
    // an error for non-JSON paths.
    if (contentType?.includes('application/json') && !requestedJsonFile) {
      // Convert blob back to text to read the error
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.error || 'Server returned an error response');
    }
    
    // Create a blob URL from the response data, preserving the content type
    const blob = new Blob([response.data], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    

    return blobUrl;
  } catch (error) {
    console.error('Error fetching import file:', error);
    
    // Enhanced error handling
    if (error.response) {
      const status = error.response.status;
      
      // Try to extract error message from response if it's JSON
      if (error.response.data && error.response.headers['content-type']?.includes('application/json')) {
        try {
          let errorData;
          if (typeof error.response.data === 'string') {
            errorData = JSON.parse(error.response.data);
          } else if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            errorData = JSON.parse(text);
          } else {
            errorData = error.response.data;
          }
          
          if (errorData.error) {
            throw new Error(errorData.error);
          }
        } catch (parseError) {
          console.warn('Could not parse error response:', parseError);
        }
      }
      
      if (status === 404) {
        throw new Error('File not found. The original file may have been moved or deleted.');
      } else if (status === 403) {
        throw new Error('Access denied. Please check your permissions.');
      } else if (status === 408) {
        throw new Error('Request timeout. The file may be too large.');
      }
    }
    
    throw new Error(`Failed to load file: ${error.message}`);
  }
};

// Bulk download import files as a zip
export const fetchImportPdfBulk = async (files) => {
  try {
    const response = await api.post(`${BASE_URL}/importPdfBulkDownload`, {
      files
    }, {
      responseType: 'blob',
      timeout: 120000 // 2 minutes for bulk downloads
    });

    if (!response.data) {
      throw new Error('No data received from bulk download');
    }

    const contentType = response.headers['content-type'];

    // Check if we received a JSON error response
    if (contentType?.includes('application/json')) {
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.error || 'Server returned an error response');
    }

    return {
      blob: response.data,
      succeeded: parseInt(response.headers['x-files-succeeded'] || '0', 10),
      failed: parseInt(response.headers['x-files-failed'] || '0', 10)
    };
  } catch (error) {
    console.error('Error in bulk PDF download:', error);
    throw new Error(`Bulk download failed: ${error.message}`);
  }
};

// ===== REPORT NOTES API =====

// Get notes for a specific report/page or search
export const getReportNotes = async (params = {}) => {
  try {
    const { orgId, locationId, reportId, pageId, noteId, search } = params;
    
    if (!orgId) {
      throw new Error('orgId is required');
    }

    const queryParams = new URLSearchParams();
    if (orgId) queryParams.append('orgId', orgId);
    if (locationId) queryParams.append('locationId', locationId);
    if (reportId) queryParams.append('reportId', reportId);
    if (pageId) queryParams.append('pageId', pageId);
    if (noteId) queryParams.append('noteId', noteId);
    if (search) queryParams.append('search', search);

    const response = await api.get(`${BASE_URL}/reportNotes?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching report notes:', error);
    throw error;
  }
};

// Create a new note
export const createReportNote = async (noteData) => {
  try {
    const response = await api.post(`${BASE_URL}/reportNotes`, noteData);
    return response.data;
  } catch (error) {
    console.error('Error creating report note:', error);
    throw error;
  }
};

// Update an existing note
export const updateReportNote = async (noteId, updateData) => {
  try {
    const response = await api.put(`${BASE_URL}/reportNotes?noteId=${noteId}`, updateData);
    return response.data;
  } catch (error) {
    console.error('Error updating report note:', error);
    throw error;
  }
};

// Delete a note (soft delete)
export const deleteReportNote = async (noteId, orgId) => {
  try {
    const response = await api.delete(`${BASE_URL}/reportNotes?noteId=${noteId}&orgId=${orgId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting report note:', error);
    throw error;
  }
};

// Get note indicators for a report (from SQL Server)
export const getReportNoteIndicators = async (orgId, locationId, reportId, pageId = null) => {
  try {
    const queryParams = new URLSearchParams({
      orgId: orgId.toString(),
      locationId: locationId.toString(),
      reportId: reportId
    });
    
    if (pageId) {
      queryParams.append('pageId', pageId);
    }

    const response = await api.get(`${BASE_URL}/reportNoteReferences?${queryParams.toString()}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching report note indicators:', error);
    throw error;
  }
};

// Add a reply to a note
export const addReportNoteReply = async (noteId, orgId, replyData) => {
  try {
    // Get the existing note first
    const noteResponse = await getReportNotes({ noteId, orgId });
    const existingNote = noteResponse.notes[0];
    
    if (!existingNote) {
      throw new Error('Note not found');
    }

    // Add the new reply
    const newReply = {
      id: `reply-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
      userId: replyData.userId,
      userDisplayName: replyData.userDisplayName,
      replyText: replyData.replyText,
      mentions: replyData.mentions || [],
      createdAt: new Date().toISOString()
    };

    const updatedReplies = [...(existingNote.replies || []), newReply];
    
    // Update the note with the new reply
    const updateData = {
      ...existingNote,
      replies: updatedReplies,
      orgId: orgId
    };

    const response = await updateReportNote(noteId, updateData);
    return response.data;
  } catch (error) {
    console.error('Error adding report note reply:', error);
    throw error;
  }
};

// Intercom JWT generation
export const getIntercomJWT = async (userId, email, name, organizationRole = null, currentOrganization = null) => {
  try {
    const payload = {
      userId,
      email,
      name,
      platform: 'web'
    };

    // Include role and org if provided - backend will update Intercom via REST API
    if (organizationRole) payload.organizationRole = organizationRole;
    if (currentOrganization) payload.currentOrganization = currentOrganization;

    const response = await api.post('/intercomHash', payload);
    return response.data;
  } catch (error) {
    console.error('Error getting Intercom JWT:', error);
    throw error;
  }
};

// Update Intercom user attributes (without JWT regeneration)
export const updateIntercomAttributes = async (userId, email, name, organizationRole, currentOrganization) => {
  try {
    const response = await api.post('/intercomHash', {
      userId,
      email,
      name,
      organizationRole,
      currentOrganization,
      updateOnly: true
    });
    return response.data;
  } catch (error) {
    console.error('Error updating Intercom attributes:', error);
    // Don't throw - this is a non-critical update
    return { success: false, error: error.message };
  }
};

// ── Agentic finance workflows ────────────────────────────────────────────────
//
// A native action (e.g. Forecast V3's "Analyze & improve accuracy") sends an
// explicit workflow ref, NOT a text prompt: the backend then runs a fixed
// evidence/analysis/proposal path instead of hoping the model picks the right
// tools. Proposal review and apply go through the workflow's own endpoints so
// every write carries the run's audit, apply reservation and staleness checks.

export const runWorkflow = async ({ workflowRef, hotelId, currentOrgId, versionId = null, asOf = null, year = null, params = null, question = null, pageContext = null, attachmentIds = null, threadId = null }) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiWorkflows/run`, {
    workflowRef, hotelId, currentOrgId, versionId, asOf, year,
    params: params || {},
    question,
    // Same envelope a chat turn sends, so the run records the page state it
    // was launched from and a later navigation cannot change it.
    pageContext,
    // Files the user attached to this conversation (notes, checklists) that
    // the workflow may read; server re-checks ownership.
    ...(Array.isArray(attachmentIds) && attachmentIds.length ? { attachmentIds } : {}),
    ...(threadId ? { threadId } : {})
  });
  return response.data;
};

// ── AI chat attachments ─────────────────────────────────────────────────────
// Base64-in-JSON like every other upload in the app (no multipart plugin on
// the fastify host). The server stores the file, extracts a markdown replica
// and runs the document-review model; the returned attachment carries the
// review summary for the composer chip.
const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export const CHAT_ATTACHMENT_ACCEPT = '.pdf,.xlsx,.xls,.xlsm,.xlsb,.csv,.tsv,.txt,.md,.json,.docx,.png,.jpg,.jpeg,.gif,.webp';
export const CHAT_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export const uploadChatAttachment = async (file, { hotelId = null, threadId = null, currentOrgId = null } = {}) => {
  if (!file) throw new Error('File is required');
  if (file.size > CHAT_ATTACHMENT_MAX_BYTES) throw new Error('File exceeds the 25MB attachment limit');
  const fileData = await readFileAsBase64(file);
  const response = await api.post(`${FASTIFY_BASE_URL}/aiAttachments`, {
    fileName: file.name,
    fileData,
    contentType: file.type || null,
    hotelId,
    threadId,
    currentOrgId
  });
  return response.data;
};

export const getChatAttachment = async (attachmentId) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiAttachments/${encodeURIComponent(attachmentId)}`);
  return response.data;
};

export const listChatAttachments = async (threadId) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiAttachments`, { params: { threadId } });
  return response.data;
};

/**
 * @param {string|null} [view] 'card' returns the shape POST /aiWorkflows/run does (WorkflowRunCard's initialRun).
 */
export const getWorkflowRun = async (runId, currentOrgId, view = null) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiWorkflows/runs/${runId}`, { params: { currentOrgId, ...(view ? { view } : {}) } });
  return response.data;
};

export const listWorkflowRuns = async ({ currentOrgId, hotelId = null, workflowKey = null, limit = 25 }) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiWorkflows/runs`, {
    params: { currentOrgId, hotelId, workflowKey, limit }
  });
  return response.data;
};

/** action: 'reviewed' | 'approved' | 'rejected' | 'deferred' */
export const decideWorkflowProposals = async ({ runId, currentOrgId, action, proposalIds = null, note = null }) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiWorkflows/runs/${runId}/decision`, {
    currentOrgId, action, proposalIds, note
  });
  return response.data;
};

export const applyWorkflowProposals = async ({ runId, currentOrgId, proposalIds = null }) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiWorkflows/runs/${runId}/apply`, {
    currentOrgId, proposalIds
  });
  return response.data;
};

// Undo applied changes: runs the revert plan the server stored at apply time
// (inverse endpoint + args) under the current user's identity.
export const revertWorkflowProposals = async ({ runId, currentOrgId, proposalIds = null }) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiWorkflows/runs/${runId}/revert`, {
    currentOrgId, proposalIds
  });
  return response.data;
};

// ── AI property onboarding ──────────────────────────────────────────────────
// A user-started session: brief → profile → sample files → AI analysis →
// questions → review → apply. Nothing reaches etl-config until apply, and an
// applied config can be reverted. See docs/ai-property-onboarding.md.

const ONBOARDING = `${FASTIFY_BASE_URL}/propertyOnboarding`;

export const listPropertyOnboardings = async ({ currentOrgId, locationId = null }) => {
  const response = await api.get(ONBOARDING, { params: { currentOrgId, locationId } });
  return response.data;
};

export const startPropertyOnboarding = async ({ currentOrgId, locationId, brief, autoSamples = true, reportKeys }) => {
  const response = await api.post(ONBOARDING, { currentOrgId, locationId, brief, autoSamples, ...(reportKeys ? { reportKeys } : {}) });
  return response.data;
};

// The AI's view of which received reports to onboard from; `message` steers it.
export const recommendPropertyOnboardingReports = async ({ currentOrgId, locationId, message = '', current = null }) => {
  const response = await api.post(`${ONBOARDING}/receivedReports/recommend`, { currentOrgId, locationId, message, current });
  return response.data;
};

export const detectPropertyOnboardingReports = async ({ currentOrgId, locationId }) => {
  const response = await api.get(`${ONBOARDING}/receivedReports`, { params: { currentOrgId, locationId } });
  return response.data;
};

export const getPropertyOnboarding = async (id, currentOrgId) => {
  const response = await api.get(`${ONBOARDING}/${encodeURIComponent(id)}`, { params: { currentOrgId } });
  return response.data;
};

export const deletePropertyOnboarding = async (id, currentOrgId) => {
  const response = await api.delete(`${ONBOARDING}/${encodeURIComponent(id)}`, { params: { currentOrgId } });
  return response.data;
};

export const updatePropertyOnboardingBrief = async (id, currentOrgId, brief) => {
  const response = await api.put(`${ONBOARDING}/${encodeURIComponent(id)}/brief`, { currentOrgId, brief });
  return response.data;
};

export const addPropertyOnboardingNote = async (id, currentOrgId, text) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/notes`, { currentOrgId, text });
  return response.data;
};

export const generatePropertyOnboardingProfile = async (id, currentOrgId) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/profile`, { currentOrgId });
  return response.data;
};

export const savePropertyOnboardingProfile = async (id, currentOrgId, { facts, confirm = false }) => {
  const response = await api.put(`${ONBOARDING}/${encodeURIComponent(id)}/profile`, { currentOrgId, facts, confirm });
  return response.data;
};

export const listPropertyOnboardingCandidateFiles = async (id, currentOrgId) => {
  const response = await api.get(`${ONBOARDING}/${encodeURIComponent(id)}/candidateFiles`, { params: { currentOrgId } });
  return response.data;
};

export const addPropertyOnboardingFile = async (id, currentOrgId, { source, ref = null, attachmentId = null }) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/files`, { currentOrgId, source, ref, attachmentId });
  return response.data;
};

export const removePropertyOnboardingFile = async (id, currentOrgId, fileId) => {
  const response = await api.delete(`${ONBOARDING}/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`, { params: { currentOrgId } });
  return response.data;
};

export const analyzePropertyOnboarding = async (id, currentOrgId) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/analyze`, { currentOrgId });
  return response.data;
};

export const answerPropertyOnboardingQuestions = async (id, currentOrgId, answers) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/answers`, { currentOrgId, answers });
  return response.data;
};

export const editPropertyOnboardingDraft = async (id, currentOrgId, edit) => {
  const response = await api.put(`${ONBOARDING}/${encodeURIComponent(id)}/draft`, { currentOrgId, ...edit });
  return response.data;
};

export const previewPropertyOnboardingApply = async (id, currentOrgId) => {
  const response = await api.get(`${ONBOARDING}/${encodeURIComponent(id)}/applyPreview`, { params: { currentOrgId } });
  return response.data;
};

export const applyPropertyOnboarding = async (id, currentOrgId, { replaceFileTypes = [], acknowledgeBlockers = false } = {}) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/apply`, { currentOrgId, replaceFileTypes, acknowledgeBlockers });
  return response.data;
};

export const revertPropertyOnboarding = async (id, currentOrgId) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/revert`, { currentOrgId });
  return response.data;
};

export const getPropertyOnboardingUnmapped = async (id, currentOrgId) => {
  const response = await api.get(`${ONBOARDING}/${encodeURIComponent(id)}/unmapped`, { params: { currentOrgId } });
  return response.data;
};

export const mapPropertyOnboardingUnmapped = async (id, currentOrgId, mappings) => {
  const response = await api.post(`${ONBOARDING}/${encodeURIComponent(id)}/unmapped`, { currentOrgId, mappings });
  return response.data;
};

export const getPropertyOnboardingMonitor = async (id, currentOrgId) => {
  const response = await api.get(`${ONBOARDING}/${encodeURIComponent(id)}/monitor`, { params: { currentOrgId } });
  return response.data;
};

// ── Workflow watches ────────────────────────────────────────────────────────
// A watch runs one of these workflows on a schedule and emails the owner when
// something needs approving. Created from a typed payload rather than from a
// sentence, for the same reason a native action sends a workflowRef: the thing
// that runs unattended should not have been assembled by a language model.

export const createWorkflowWatch = async ({
  workflowRef, hotelId, currentOrgId,
  versionId = null, year = null, versionName = null, issuePatternKey = null,
  frequency = 'daily', time = '07:00', daysOfWeek = null, timezone = null,
  recipients = null, materialityAmount = null, minConfidence = null, notifyOn = null,
  name = null
}) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiWorkflows/watches`, {
    workflowRef, hotelId, currentOrgId, versionId, year, versionName, issuePatternKey,
    frequency, time, daysOfWeek, timezone,
    recipients, materialityAmount, minConfidence, notifyOn, name
  });
  return response.data;
};

export const listWorkflowWatches = async ({ currentOrgId, hotelId = null }) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiWorkflows/watches`, {
    params: { currentOrgId, hotelId }
  });
  return response.data;
};

export const listWatchableWorkflows = async () => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiWorkflows/watchable`);
  return response.data;
};

// AI Chat API functions
export const queryHotelData = async (hotelId, question, threadId = null, currentOrgId = null, model = null, promptKey = null, selectedLocationIds = null, pageContext = null, attachmentIds = null) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/queryHotelData`, {
      hotelId,
      question,
      threadId,
      currentOrgId,
      model, // Optional AI model override
      promptKey, // Optional constellation prompt key for fast-path handling
      // assistant_context.v1 envelope for the page the user is on. Optional —
      // the server authorizes every id in it and works fine without one.
      pageContext,
      // Persisted alongside the session so reloads can restore the exact
      // multi-select scope (may differ from hotelId when user picked a
      // subset of properties instead of collapsing to 'all').
      selectedLocationIds,
      ...(Array.isArray(attachmentIds) && attachmentIds.length ? { attachmentIds } : {})
    });
    return response.data;
  } catch (error) {
    console.error('Error querying hotel data:', error);
    throw error;
  }
};

/**
 * Streaming version of queryHotelData using Server-Sent Events.
 *
 * Uses fetch (not axios) because axios doesn't support SSE streaming.
 * Replicates the same auth header logic from the axios interceptor.
 *
 * @param {Object} params - { hotelId, question, threadId, currentOrgId, model, promptKey }
 * @param {Object} callbacks - { onToken, onStatus, onDone, onError, onThinking }
 * @returns {Function} abort - call to cancel the stream
 */
export const queryHotelDataStream = (params, callbacks) => {
  const { onToken, onStatus, onDone, onError, onThinking, onPnlReport, onForecastProposal, onFileReferences, onDataTable, onTraceStep, onWorkflowRun } = callbacks;
  const controller = new AbortController();

  (async () => {
    // Once the server has accepted the turn it keeps running even if the
    // connection drops, so a retry would run the whole turn a second time.
    let accepted = false;
    try {
      // Build auth headers (same logic as axios interceptor)
      const headers = { 'Content-Type': 'application/json' };

      const accessTokenKey = Object.keys(localStorage).find(k => k.endsWith('.accessToken'));
      const idTokenKey = Object.keys(localStorage).find(k => k.endsWith('.idToken'));

      if (accessTokenKey) headers['Authorization'] = `Bearer ${localStorage.getItem(accessTokenKey)}`;
      if (idTokenKey) headers['x-id-token'] = localStorage.getItem(idTokenKey);

      // Emulation support
      const { getEmulatedUserForApi } = await import('./emulation');
      const emulatedUser = getEmulatedUserForApi();
      if (emulatedUser) {
        headers['x-user-id'] = emulatedUser.userId;
        headers['x-user-email'] = emulatedUser.userEmail;
        headers['x-emulating'] = 'true';
      } else if (accessTokenKey) {
        try {
          const payload = JSON.parse(atob(localStorage.getItem(accessTokenKey).split('.')[1]));
          if (payload.username || payload.sub) headers['x-user-id'] = payload.username || payload.sub;
          if (payload.email) headers['x-user-email'] = payload.email;
        } catch { /* ignore */ }
      }

      const response = await fetch(`${FASTIFY_BASE_URL}/queryHotelData/stream`, {
        method: 'POST',
        headers,
        // pagePath lets the server suggest a Help Center guide for this page;
        // pageUrl (with the page's filters) lets help answers link back into it.
        body: JSON.stringify({ ...params, pagePath: params.pagePath || window.location.pathname, pageUrl: window.location.pathname + window.location.search }),
        signal: controller.signal
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${response.status}`);
      }
      accepted = true;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let sawTerminalEvent = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse complete SSE events. An event may contain multi-line data
        // fields, and event/data lines can split across network chunks.
        let boundary;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const rawEvent = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          if (!rawEvent.trim()) continue;

          let currentEvent = 'message';
          let dataStr = '';
          for (const line of rawEvent.split('\n')) {
            if (line.startsWith('event:')) {
              currentEvent = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataStr += line.slice(5).trim();
            }
          }
          if (dataStr && currentEvent) {
            try {
              const data = JSON.parse(dataStr);
              if (currentEvent !== 'token' && currentEvent !== 'thinking') {
                (window.__console || console).log(`[SSE] event=${currentEvent}`, currentEvent === 'pnl_report' ? `(${dataStr.length} chars)` : '');
              }
              switch (currentEvent) {
                case 'token':
                  onToken?.(data.content);
                  break;
                case 'thinking':
                  onThinking?.(data.content);
                  break;
                case 'status':
                  onStatus?.(data.message);
                  break;
                case 'done':
                  sawTerminalEvent = true;
                  onDone?.(data);
                  break;
                case 'pnl_report':
                  (window.__console || console).log('[SSE] pnl_report received, data keys:', data.data ? Object.keys(data.data) : 'null');
                  onPnlReport?.(data.data);
                  break;
                case 'forecast_proposal':
                  (window.__console || console).log('[SSE] forecast_proposal received, changes:', data.data?.changes?.length || 0);
                  onForecastProposal?.(data.data);
                  break;
                case 'file_references':
                  (window.__console || console).log('[SSE] file_references received, files:', data.files?.length || 0);
                  onFileReferences?.(data.files || []);
                  break;
                case 'data_table':
                  (window.__console || console).log('[SSE] data_table received, rows:', data.data?.rows?.length || 0);
                  onDataTable?.(data.data);
                  break;
                case 'trace_step':
                  onTraceStep?.(data.data);
                  break;
                case 'workflow_run':
                  // The server routed this question to a deterministic workflow.
                  // Same stored shape the native action returns, so one card
                  // renders both paths.
                  onWorkflowRun?.(data);
                  break;
                case 'error':
                  sawTerminalEvent = true;
                  onError?.(new Error(data.error));
                  break;
              }
            } catch (parseErr) {
              (window.__console || console).warn(`[SSE] Failed to parse ${currentEvent} data:`, parseErr.message, dataStr?.substring(0, 200));
            }
          }
        }
      }

      if (!sawTerminalEvent) {
        throw new Error('Stream ended before the assistant completed a response.');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        if (accepted) err.streamInterrupted = true;
        onError?.(err);
      }
    }
  })();

  return () => controller.abort();
};

export const getAIModels = async () => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/getAIModels`);
    return response.data;
  } catch (error) {
    console.error('Error getting AI models:', error);
    throw error;
  }
};

export const submitAIChatFeedback = async (feedbackData) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiChatFeedback`, feedbackData);
    return response.data;
  } catch (error) {
    console.error('Error submitting AI chat feedback:', error);
    throw error;
  }
};

// ── AI skills (playbooks) — admin only ──────────────────────────────────────
export const listAiSkills = async (params = {}) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiSkills`, { params });
  return response.data;
};

export const getAiSkill = async (id) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiSkills/${encodeURIComponent(id)}`);
  return response.data;
};

export const createAiSkill = async (skill) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiSkills`, skill);
  return response.data;
};

export const updateAiSkill = async (id, patch) => {
  const response = await api.patch(`${FASTIFY_BASE_URL}/aiSkills/${encodeURIComponent(id)}`, patch);
  return response.data;
};

export const setAiSkillStatus = async (id, status, note = null) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiSkills/${encodeURIComponent(id)}/status`, { status, note });
  return response.data;
};

export const deleteAiSkill = async (id) => {
  const response = await api.delete(`${FASTIFY_BASE_URL}/aiSkills/${encodeURIComponent(id)}`);
  return response.data;
};

export const listAiSkillReflections = async (params = {}) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiSkills/reflections`, { params });
  return response.data;
};

export const runAiSkillReflection = async (threadId, userId = null) => {
  const response = await api.post(`${FASTIFY_BASE_URL}/aiSkills/reflect`, { threadId, userId });
  return response.data;
};

export const getGlTransactionDetail = async (orgId, locationId, chartOfAccountId, startDate, endDate) => {
  try {
    const response = await api.get(API_ENDPOINTS.TRANSACTIONS, {
      params: { orgId, locationId, chartOfAccountId, startDate, endDate, limit: 500 }
    });
    // Normalize to match what PnLReportTable expects
    const data = response.data.data || [];
    return {
      transactions: data,
      total: data.length,
      summary: {
        totalAmount: data.reduce((sum, r) => sum + (r.transactionAmount || 0), 0)
      }
    };
  } catch (error) {
    console.error('Error fetching GL transaction detail:', error);
    throw error;
  }
};

// Last month each account saw non-zero net GL activity — lets mapping screens
// tell a live account apart from a dead one. Returns [] on failure so callers can
// treat activity as decoration rather than required data.
export const getAccountActivity = async (orgId, locationId, accountType = null) => {
  if (!orgId || !locationId) return [];
  try {
    const response = await api.get(API_ENDPOINTS.TRANSACTIONS, {
      params: { summary: 'accountActivity', orgId, locationId, ...(accountType ? { accountType } : {}) }
    });
    return response.data?.data || [];
  } catch (error) {
    console.error('Error fetching account activity:', error);
    return [];
  }
};

export const getAIChatSourceFile = async (fileId) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/getAIChatSourceFile`, {
      params: { fileId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI chat source file:', error);
    throw error;
  }
};

// Dashboard Daily Briefing — SSE streaming endpoint.
// Events:
//   cached:       { data, generatedAt, model }   — immediate if today's cache exists
//   regenerating: { reason, cachedTotal, liveTotal, delta } — fires when backend detects stale
//                                                             cache (data grew). UI should show
//                                                             "Regenerating…" while keeping the
//                                                             cached content visible.
//   status:       { message }                     — pipeline progress
//   token:        { content }                     — raw model tokens as they stream
//   done:         { cached, data?, generatedAt?, model?, regenerated? }
//   error:        { error }
//
// Returns an abort function.
export const streamDashboardDailyBriefing = (
  { hotelId, currentOrgId, currency, force, comparisonMode, comparisonLabel },
  { onCached, onRegenerating, onStatus, onToken, onDone, onError } = {}
) => {
  const params = new URLSearchParams();
  if (hotelId) params.set('hotelId', hotelId);
  if (currentOrgId) params.set('currentOrgId', currentOrgId);
  // Currency is part of the cache key on the server. NULL → USD on both sides
  // so legacy orgs see no behavior change.
  if (currency) params.set('currency', currency);
  if (force) params.set('force', '1');
  if (comparisonMode) params.set('comparisonMode', comparisonMode);
  if (comparisonLabel) params.set('comparisonLabel', comparisonLabel);
  const url = `${FASTIFY_BASE_URL}/dashboardDailyBriefing?${params.toString()}`;

  const controller = new AbortController();

  (async () => {
    try {
      const headers = { 'Accept': 'text/event-stream' };
      const accessTokenKey = Object.keys(localStorage).find(k => k.endsWith('.accessToken'));
      const idTokenKey = Object.keys(localStorage).find(k => k.endsWith('.idToken'));
      if (accessTokenKey) headers['Authorization'] = `Bearer ${localStorage.getItem(accessTokenKey)}`;
      if (idTokenKey) headers['x-id-token'] = localStorage.getItem(idTokenKey);

      const { getEmulatedUserForApi } = await import('./emulation');
      const emulatedUser = getEmulatedUserForApi();
      if (emulatedUser) {
        headers['x-user-id'] = emulatedUser.userId;
        headers['x-user-email'] = emulatedUser.userEmail;
        headers['x-emulating'] = 'true';
      } else if (accessTokenKey) {
        try {
          const payload = JSON.parse(atob(localStorage.getItem(accessTokenKey).split('.')[1]));
          if (payload.username || payload.sub) headers['x-user-id'] = payload.username || payload.sub;
          if (payload.email) headers['x-user-email'] = payload.email;
        } catch { /* ignore */ }
      }

      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers,
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        onError?.(text || `HTTP ${response.status}`);
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = 'message', data = '';
          for (const line of frame.split('\n')) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) data += line.slice(5).trim();
          }
          if (!data) continue;
          let payload;
          try { payload = JSON.parse(data); } catch { continue; }
          if (event === 'cached') onCached?.(payload);
          else if (event === 'regenerating') onRegenerating?.(payload);
          else if (event === 'status') onStatus?.(payload.message);
          else if (event === 'token') onToken?.(payload.content);
          else if (event === 'done') onDone?.(payload);
          else if (event === 'error') onError?.(payload.error);
        }
      }
    } catch (e) {
      if (e.name !== 'AbortError') onError?.(e.message || 'Stream failed');
    }
  })();

  return () => controller.abort();
};

// AI Chat Sessions
export const getAIChatSessions = async (hotelId, orgId, { limit = 200 } = {}) => {
  try {
    const params = {};
    if (hotelId) params.hotelId = hotelId;
    // Most-recent-first cap: the rail/tray render a handful and the tray's
    // search runs over what it holds; unbounded lists ran to thousands of
    // rows (~840KB) on every dashboard load. Pass { limit: 0 } for all.
    if (limit > 0) params.limit = limit;
    // Scoping by orgId prevents the history tray / auto-resume from
    // surfacing sessions created under a different org the user has
    // since switched away from (2026-04-22 cross-org scope-pin bug).
    if (orgId) params.orgId = orgId;
    const response = await api.get(`${FASTIFY_BASE_URL}/aiChatSessions`, { params });
    return response.data;
  } catch (error) {
    console.error('Error getting AI chat sessions:', error);
    throw error;
  }
};

// Dry-run preview of a scheduled task's report (nothing is sent). Powers the
// in-chat EmailPreviewCard markup flow.
export const getScheduledTaskPreview = async (taskId) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiScheduledTasks/preview`, { params: { taskId } });
  return response.data; // { taskId, name, subject, html, executionMode, stepDiagnostics }
};

export const getAIChatSession = async (threadId) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/aiChatSessions`, {
      params: { threadId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI chat session:', error);
    throw error;
  }
};

export const saveAIChatSession = async (sessionData) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiChatSessions`, sessionData);
    return response.data;
  } catch (error) {
    console.error('Error saving AI chat session:', error);
    throw error;
  }
};

export const deleteAIChatSession = async (sessionId) => {
  try {
    const response = await api.delete(`${FASTIFY_BASE_URL}/aiChatSessions`, {
      params: { sessionId }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting AI chat session:', error);
    throw error;
  }
};

// Fork a session to one or more recipients. The backend creates a new
// recipient-owned Cosmos item per recipient (point-in-time snapshot) so
// subsequent turns on either side stay isolated. Recipient IDs are emails
// (the /userId partition key for ai-chat-sessions is the user's email).
export const shareAIChatSession = async (sessionId, recipientUserIds) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiChatSessions/share`, {
      sessionId,
      recipientUserIds
    });
    return response.data;
  } catch (error) {
    console.error('Error sharing AI chat session:', error);
    throw error;
  }
};

export const getThreadMessages = async (threadId) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/getThreadMessages`, {
      params: { threadId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting thread messages:', error);
    throw error;
  }
};

// =============================================================================
// AI EXTRACTION API FUNCTIONS
// =============================================================================

/**
 * Trigger AI extraction for a document
 * @param {object} params - Extraction parameters
 * @param {number} params.orgId - Organization ID
 * @param {number} params.locationId - Location ID
 * @param {string} params.documentContent - Extracted text/table content from document
 * @param {string} params.sourceFileName - Name of the source file
 * @param {string} params.sourceFileType - File type (pdf, xlsx, csv, txt)
 * @param {string} params.targetSchema - Target Lodgeic schema (hotelRevenue, hotelRoomStats, etc.)
 * @param {number} [params.templateId] - Optional template ID for guidance
 * @param {string} [params.preferredModel] - AI model preference (auto, haiku, sonnet)
 */
export const triggerAiExtraction = async (params) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiExtraction`, params);
    return response.data;
  } catch (error) {
    console.error('Error triggering AI extraction:', error);
    throw error;
  }
};

/**
 * Get AI extraction by ID
 * @param {string} extractionId - Extraction ID (UUID)
 * @param {number} orgId - Organization ID (required for Cosmos DB partition key)
 */
export const getAiExtraction = async (extractionId, orgId) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/aiExtraction`, {
      params: { id: extractionId, orgId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI extraction:', error);
    throw error;
  }
};

/**
 * Get AI extraction templates
 * @param {number} orgId - Organization ID
 * @param {number} [locationId] - Optional location ID
 */
export const getAiExtractionTemplates = async (orgId, locationId = null) => {
  try {
    const params = { orgId };
    if (locationId) params.locationId = locationId;

    const response = await api.get(`${FASTIFY_BASE_URL}/aiExtractionTemplate`, { params });
    return response.data;
  } catch (error) {
    console.error('Error getting AI extraction templates:', error);
    throw error;
  }
};

/**
 * Get a single AI extraction template
 * @param {string} templateId - Template ID (UUID)
 * @param {number} orgId - Organization ID (required for Cosmos DB partition key)
 */
export const getAiExtractionTemplate = async (templateId, orgId) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/aiExtractionTemplate`, {
      params: { id: templateId, orgId }
    });
    return response.data;
  } catch (error) {
    console.error('Error getting AI extraction template:', error);
    throw error;
  }
};

/**
 * Create AI extraction template
 * @param {object} templateData - Template configuration
 */
export const createAiExtractionTemplate = async (templateData) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiExtractionTemplate`, templateData);
    return response.data;
  } catch (error) {
    console.error('Error creating AI extraction template:', error);
    throw error;
  }
};

/**
 * Update AI extraction template
 * @param {string} templateId - Template ID (UUID)
 * @param {number} orgId - Organization ID (required for Cosmos DB partition key)
 * @param {object} templateData - Updated template data
 */
export const updateAiExtractionTemplate = async (templateId, orgId, templateData) => {
  try {
    const response = await api.put(`${FASTIFY_BASE_URL}/aiExtractionTemplate`, templateData, {
      params: { id: templateId, orgId }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating AI extraction template:', error);
    throw error;
  }
};

/**
 * Delete AI extraction template
 * @param {string} templateId - Template ID (UUID)
 * @param {number} orgId - Organization ID (required for Cosmos DB partition key)
 */
export const deleteAiExtractionTemplate = async (templateId, orgId) => {
  try {
    const response = await api.delete(`${FASTIFY_BASE_URL}/aiExtractionTemplate`, {
      params: { id: templateId, orgId }
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting AI extraction template:', error);
    throw error;
  }
};

/**
 * Update AI extraction with user-confirmed mappings
 * @param {string} extractionId - Extraction ID (UUID)
 * @param {number} orgId - Organization ID (required for Cosmos DB partition key)
 * @param {object} data - Update data including userMappings and optional template save options
 */
export const updateAiExtraction = async (extractionId, orgId, data) => {
  try {
    const response = await api.put(`${FASTIFY_BASE_URL}/aiExtraction`, data, {
      params: { id: extractionId, orgId }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating AI extraction:', error);
    throw error;
  }
};

/**
 * Import AI-extracted data into database (hotelRevenue, hotelRoomStats)
 * @param {object} params - Import parameters
 * @param {string} params.extractionId - ID of the extraction to import (from Cosmos DB)
 * @param {number} params.orgId - Organization ID
 * @param {number} params.locationId - Location ID
 * @param {object} [params.extractedData] - Optional: pass data directly instead of using extractionId
 * @param {string} [params.businessDate] - Optional: business date if passing data directly
 * @param {Array<string>} [params.dataTypes] - Optional: data types being imported
 */
export const importAiExtractedData = async (params) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiExtractionImport`, params);
    return response.data;
  } catch (error) {
    console.error('Error importing AI extracted data:', error);
    throw error;
  }
};

// =============================================================================
// NATIVE REPORT SUBSCRIPTION API FUNCTIONS
// =============================================================================

/**
 * Get native report subscriptions
 * @param {object} params - Query parameters { userId, orgId, isActive }
 */
export const getNativeReportSubscriptions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.userId) queryParams.append('userId', params.userId);
    if (params.orgId) queryParams.append('orgId', params.orgId);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);

    const url = `${BASE_URL}/nativeReportSubscription${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);

    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching native report subscriptions:', error);
    throw error;
  }
};

/**
 * Get a specific native report subscription by ID
 * @param {string} id - Subscription ID
 */
export const getNativeReportSubscription = async (id) => {
  try {
    const response = await api.get(`${BASE_URL}/nativeReportSubscription/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching native report subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new native report subscription
 * @param {object} subscriptionData - Subscription configuration
 */
export const createNativeReportSubscription = async (subscriptionData) => {
  try {
    if (!subscriptionData.userId || !subscriptionData.reportType || !subscriptionData.orgId) {
      throw new Error('userId, reportType, and orgId are required');
    }

    if (!subscriptionData.locations || subscriptionData.locations.length === 0) {
      throw new Error('At least one location is required');
    }

    const response = await api.post(`${BASE_URL}/nativeReportSubscription`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error('Error creating native report subscription:', error);
    throw error;
  }
};

/**
 * Update an existing native report subscription
 * @param {string} id - Subscription ID
 * @param {object} subscriptionData - Updated subscription configuration
 */
export const updateNativeReportSubscription = async (id, subscriptionData) => {
  try {
    const response = await api.put(`${BASE_URL}/nativeReportSubscription/${id}`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error(`Error updating native report subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Delete (soft delete) a native report subscription
 * @param {string} id - Subscription ID
 */
export const deleteNativeReportSubscription = async (id) => {
  try {
    const response = await api.delete(`${BASE_URL}/nativeReportSubscription/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting native report subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Toggle native report subscription status (active/inactive)
 * @param {string} id - Subscription ID
 * @param {boolean} isActive - New active status
 */
export const toggleNativeReportSubscriptionStatus = async (id, isActive) => {
  try {
    const response = await api.put(`${BASE_URL}/nativeReportSubscription/${id}`, { isActive });
    return response.data;
  } catch (error) {
    console.error(`Error toggling native report subscription ${id} status:`, error);
    throw error;
  }
};

/**
 * Send a test native report
 * @param {string} id - Subscription ID
 * @param {string} effectiveDate - Optional date string (YYYY-MM-DD) to use as the report date
 */
export const testNativeReportSubscription = async (id, effectiveDate) => {
  try {
    const response = await api.post(`${BASE_URL}/nativeReportSubscription/${id}/test`, {
      ...(effectiveDate && { effectiveDate })
    });
    return response.data;
  } catch (error) {
    console.error(`Error testing native report subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Validate native report subscription data
 * @param {object} subscriptionData - Subscription configuration to validate
 */
export const validateNativeReportSubscription = (subscriptionData) => {
  const errors = [];

  if (!subscriptionData.reportType) {
    errors.push('Report type is required');
  }

  if (!subscriptionData.locations || subscriptionData.locations.length === 0) {
    errors.push('At least one location is required');
  }

  if (!subscriptionData.triggerTime?.frequency) {
    errors.push('Frequency is required');
  }

  if (!subscriptionData.triggerTime?.time) {
    errors.push('Time is required');
  }

  if (!subscriptionData.triggerTime?.timezone) {
    errors.push('Timezone is required');
  }

  if (!subscriptionData.deliveryConfig?.recipients || subscriptionData.deliveryConfig.recipients.length === 0) {
    errors.push('At least one recipient email is required');
  }

  // Validate email format for recipients
  if (subscriptionData.deliveryConfig?.recipients) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    subscriptionData.deliveryConfig.recipients.forEach((email, index) => {
      if (!emailRegex.test(email)) {
        errors.push(`Invalid email format for recipient ${index + 1}: ${email}`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// =============================================================================
// USER ACCESSIBLE LOCATIONS API (for delegated subscription creation)
// =============================================================================

/**
 * Get locations that a specific user has access to within an organization
 * Used when creating subscriptions on behalf of other users
 *
 * @param {string} targetEmail - Email of the target user
 * @param {string} orgCode - Organization code (e.g., 'COHO', 'DEMO')
 * @returns {Promise<Object>} - { userId, orgId, orgCode, role, isOrgAdmin, locations, allLocationsAccessible }
 */
export const getUserAccessibleLocations = async (targetEmail, orgCode) => {
  try {
    const response = await api.get(`${BASE_URL}/getUserAccessibleLocations`, {
      params: {
        targetEmail,
        orgId: orgCode // Parameter name is orgId but expects org CODE
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user accessible locations:', error);
    throw error;
  }
};

/**
 * Get all users in an organization that the current user has access to
 * Uses the existing userSettings API with organization filtering
 *
 * @param {string} orgCode - Organization code (e.g., 'DEMO', 'PROD')
 * @returns {Promise<Array>} - Array of user objects
 */
export const getUsersInOrganization = async (orgCode) => {
  try {
    // The userSettings API already filters users based on shared organization access
    const response = await api.get(`${BASE_URL}/userSettings`);
    // API returns { users: [...], pagination: {...} } - extract the users array
    const allUsers = response.data.users || [];

    // Filter to users who have access to the specified org (by org CODE, not numeric ID)
    const usersInOrg = allUsers.filter(user => {
      return user.orgAccess && user.orgAccess[orgCode];
    });

    return usersInOrg;
  } catch (error) {
    console.error('Error fetching users in organization:', error);
    throw error;
  }
};

// =============================================================================
// TYPE CLASSIFICATIONS API (global lookup table for revenue/position types)
// =============================================================================

/**
 * Get all type classifications (global lookup - not org-specific)
 * @returns {Promise<Array>} - Array of type classification objects
 */
export const getTypeClassifications = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.TYPE_CLASSIFICATIONS);
    return response.data;
  } catch (error) {
    console.error('Error fetching type classifications:', error);
    throw error;
  }
};

/**
 * Get a single type classification by ID
 * @param {number} id - Type classification ID
 * @returns {Promise<Object>} - Type classification object
 */
export const getTypeClassificationById = async (id) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.TYPE_CLASSIFICATIONS}?id=${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching type classification by id:', error);
    throw error;
  }
};

/**
 * Create a new type classification
 * @param {Object} data - { name: string }
 * @returns {Promise<Object>} - Created type classification object
 */
export const createTypeClassification = async (data) => {
  try {
    const response = await api.post(API_ENDPOINTS.TYPE_CLASSIFICATIONS, data);
    return response.data;
  } catch (error) {
    console.error('Error creating type classification:', error);
    throw error;
  }
};

/**
 * Update a type classification
 * @param {number} id - Type classification ID
 * @param {Object} data - { name: string }
 * @returns {Promise<Object>} - Updated type classification object
 */
export const updateTypeClassification = async (id, data) => {
  try {
    const response = await api.put(`${API_ENDPOINTS.TYPE_CLASSIFICATIONS}?id=${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating type classification:', error);
    throw error;
  }
};

/**
 * Delete a type classification (soft delete)
 * @param {number} id - Type classification ID
 * @returns {Promise<void>}
 */
export const deleteTypeClassification = async (id) => {
  try {
    await api.delete(`${API_ENDPOINTS.TYPE_CLASSIFICATIONS}?id=${id}`);
  } catch (error) {
    console.error('Error deleting type classification:', error);
    throw error;
  }
};

// =============================================================================
// AI SUMMARY SUBSCRIPTION API FUNCTIONS
// =============================================================================

/**
 * Get AI summary subscriptions
 * @param {object} params - Query parameters { orgId }
 */
export const getAISummarySubscriptions = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.orgId) queryParams.append('orgId', params.orgId);

    const url = `${FASTIFY_BASE_URL}/aiSummarySubscription${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await api.get(url);

    return response.data?.subscriptions || [];
  } catch (error) {
    console.error('Error fetching AI summary subscriptions:', error);
    throw error;
  }
};

/**
 * Get AI summary default prompts
 */
export const getAISummaryDefaults = async () => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/aiSummarySubscription?includeDefaults=true`);
    return response.data;
  } catch (error) {
    console.error('Error fetching AI summary defaults:', error);
    throw error;
  }
};

/**
 * Get a specific AI summary subscription by ID
 * @param {string} id - Subscription ID
 */
export const getAISummarySubscription = async (id) => {
  try {
    const response = await api.get(`${FASTIFY_BASE_URL}/aiSummarySubscription?id=${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching AI summary subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new AI summary subscription
 * @param {object} subscriptionData - Subscription configuration
 */
export const createAISummarySubscription = async (subscriptionData) => {
  try {
    const response = await api.post(`${FASTIFY_BASE_URL}/aiSummarySubscription`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error('Error creating AI summary subscription:', error);
    throw error;
  }
};

/**
 * Update an existing AI summary subscription
 * @param {string} id - Subscription ID
 * @param {object} subscriptionData - Updated subscription configuration
 */
export const updateAISummarySubscription = async (id, subscriptionData) => {
  try {
    const response = await api.put(`${FASTIFY_BASE_URL}/aiSummarySubscription?id=${id}`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error(`Error updating AI summary subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Delete (soft delete) an AI summary subscription
 * @param {string} id - Subscription ID
 */
export const deleteAISummarySubscription = async (id) => {
  try {
    const response = await api.delete(`${FASTIFY_BASE_URL}/aiSummarySubscription?id=${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting AI summary subscription ${id}:`, error);
    throw error;
  }
};

/**
 * Toggle AI summary subscription status (active/inactive)
 * @param {string} id - Subscription ID
 * @param {boolean} isActive - New active status
 */
export const toggleAISummarySubscriptionStatus = async (id, isActive) => {
  try {
    const response = await api.put(`${FASTIFY_BASE_URL}/aiSummarySubscription?id=${id}`, { isActive });
    return response.data;
  } catch (error) {
    console.error(`Error toggling AI summary subscription ${id} status:`, error);
    throw error;
  }
};

/**
 * Test an AI summary subscription by sending a test email
 * @param {string} id - Subscription ID
 */
export const testAISummarySubscription = async (id) => {
  const response = await api.post(`${BASE_URL}/aiSummarySubscription/${id}/test`, {});
  return response.data;
};

// =============================================================================
// AI SCHEDULED TASKS API FUNCTIONS (agent-created recurring email tasks)
// =============================================================================

/**
 * List the caller's AI scheduled tasks (paused ones included).
 */
export const getAIScheduledTasks = async () => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiScheduledTasks?includeInactive=true&includeDeleted=true`);
  return response.data?.tasks || [];
};

/**
 * Render a dry-run preview of a task's email (nothing is sent).
 * @param {string} taskId
 */
export const previewAIScheduledTask = async (taskId) => {
  const response = await api.get(`${FASTIFY_BASE_URL}/aiScheduledTasks/preview?taskId=${encodeURIComponent(taskId)}`);
  return response.data;
};

/**
 * Run a task immediately, delivering a test-labelled email to its recipients.
 * @param {string} taskId
 */
export const runAIScheduledTaskNow = async (taskId) => {
  // "Run now" is an explicit user-authorized test delivery. Match the
  // assistant tool's semantics and bypass the local scheduled-delivery kill
  // switch while keeping the subject test-labelled. The previous request
  // omitted forceSend, so the UI reported a completed material finding while
  // LOCAL_PREVENT_EMAILS_ALERTS silently prevented its promised test email.
  const response = await api.post(`${FASTIFY_BASE_URL}/aiScheduledTasks/execute`, {
    taskId,
    isTest: true,
    forceSend: true
  });
  return response.data;
};

/**
 * Pause or resume a task. Resuming recomputes the next run server-side.
 * @param {string} taskId
 * @param {boolean} isActive
 */
export const setAIScheduledTaskActive = async (taskId, isActive) => {
  const response = await api.patch(`${FASTIFY_BASE_URL}/aiScheduledTasks/${encodeURIComponent(taskId)}`, { isActive });
  return response.data;
};

/**
 * Soft-delete a task — it stops running and disappears from the list.
 * @param {string} taskId
 */
export const deleteAIScheduledTask = async (taskId) => {
  const response = await api.delete(`${FASTIFY_BASE_URL}/aiScheduledTasks/${encodeURIComponent(taskId)}`);
  return response.data;
};

/**
 * Staged scheduling writes.
 *
 * The assistant never commits an automation directly — it validates the
 * request, stages the exact record, and shows this. Approving is what
 * performs the write, so the spec is read from the server rather than from
 * the chat message: what you approve is what will run.
 */
export const getPendingScheduledAction = async (pendingActionId) => {
  const response = await api.get(
    `${FASTIFY_BASE_URL}/aiScheduledTasks/pending/${encodeURIComponent(pendingActionId)}`
  );
  return response.data;
};

export const approvePendingScheduledAction = async (pendingActionId) => {
  const response = await api.post(
    `${FASTIFY_BASE_URL}/aiScheduledTasks/pending/${encodeURIComponent(pendingActionId)}/approve`
  );
  return response.data;
};

export const declinePendingScheduledAction = async (pendingActionId) => {
  const response = await api.post(
    `${FASTIFY_BASE_URL}/aiScheduledTasks/pending/${encodeURIComponent(pendingActionId)}/decline`
  );
  return response.data;
};

export default {
  ...api,
  logUserActivity, // Export the new function
  getEtlConfigForHotel, // Export the ETL config function
  uploadFileForEtl, // Export the file upload ETL function
  
  // Schema Builder API functions
  convertFile,
  processFormRecognizerFile,
  getSchemaConfig,
  saveSchemaConfig,
  deleteSchemaConfig,
  testSchemaMapping,
  
  // Hotel Revenue API functions
  getHotelRevenue,
  getHotelRevenueById,
  createHotelRevenue,
  updateHotelRevenue,
  deleteHotelRevenue,
  
  // Hotel Room Stats API functions
  getHotelRoomStats,
  getHotelRoomStatsById,
  createHotelRoomStats,
  updateHotelRoomStats,
  deleteHotelRoomStats,
  
  // Imports API functions
  getImports,
  getImportById,
  createImport,
  updateImport,
  deleteImport,
  fetchImportPdf,
  
  // User Management API functions
  getAllUsers,
  getUsersPaginated,
  searchUsersForMentions,
  getUserTags,
  saveUserTags,
  addUserTag,
  deleteUserTag,
  incrementTagUsage,
  adminPasswordReset,
  
  // Report Notes API functions
  getReportNotes,
  createReportNote,
  updateReportNote,
  deleteReportNote,
  getReportNoteIndicators,
  addReportNoteReply,
  
  // AI Chat API functions
  queryHotelData,
  queryHotelDataStream,
  submitAIChatFeedback,
  getAIChatSourceFile,
  getAIChatSessions,
  saveAIChatSession,
  deleteAIChatSession,
  shareAIChatSession,
  getThreadMessages,

  // AI Extraction API functions
  triggerAiExtraction,
  getAiExtraction,
  getAiExtractionTemplates,
  getAiExtractionTemplate,
  createAiExtractionTemplate,
  updateAiExtractionTemplate,
  deleteAiExtractionTemplate,
  updateAiExtraction,
  importAiExtractedData,

  // Type Classifications API functions
  getTypeClassifications,
  getTypeClassificationById,
  createTypeClassification,
  updateTypeClassification,
  deleteTypeClassification,

  // AI Summary Subscription API functions
  getAISummarySubscriptions,
  getAISummaryDefaults,
  getAISummarySubscription,
  createAISummarySubscription,
  updateAISummarySubscription,
  deleteAISummarySubscription,
  toggleAISummarySubscriptionStatus,
  testAISummarySubscription,

  // other exports
};

// ---------------------------------------------------------------------------
// Night Audit Admin Config — frequency settings per location
// ---------------------------------------------------------------------------

export const getNightAuditAdminConfigs = async () => {
    const response = await api.get(API_ENDPOINTS.NIGHT_AUDIT_ADMIN_CONFIG);
    return response.data;
};

export const updateNightAuditAdminConfig = async (payload) => {
    const response = await api.patch(API_ENDPOINTS.NIGHT_AUDIT_ADMIN_CONFIG, payload);
    return response.data;
};

// Re-evaluate a stored document against the current report-feed recognition rules.
export const sendEtlDocumentToReportFeed = async (id, { emailSubject, fileName }) => {
  const response = await api.post(`${BASE_URL}/etlDocuments`, { id, emailSubject, fileName }, { timeout: 120000 });
  return response.data;
};
